import { Component, Fragment } from "react";

import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Fieldset } from "primereact/fieldset";
import { AutoComplete } from "primereact/autocomplete";
import { InputText } from "primereact/inputtext";

import { InvoiceService } from "../InvoiceService";
import { InvoiceListService } from "../../xu-ly-sai-sot/InvoiceListService";
import { TenantCompanyService } from "../../he-thong/doanh-nghiep/thong-tin-doanh-nghiep/TenantCompanyService";
import {
  BusinessLocationService,
  RegisterInvoiceBusinessLocation,
} from "../../danh-muc/dia-diem-kinh-doanh/BusinessLocationService";
import { RegisterInvoiceService } from "../../../service/invoice/RegisterInvoiceService";
import { InvoiceSettingDefaultService } from "../../he-thong/cau-hinh-hoa-don/InvoiceSettingDefaultService";
import { TaxService } from "../../danh-muc/thue-suat/TaxService";
import { TenantSettingService } from "../../he-thong/tham-so-he-thong/TenantSettingService";

import { AppStorage } from "../../../service/UserStorage";

import { ToastService } from "../../../AppToast";
import { i18n, translate, translateError } from "../../../shared/language";

import {
  INVOICE_DETAIL_REMOVED_FIELDS,
  InvoiceField,
} from "../item/Initial";
import {
  HouseholdBusinessLocationNoticeDialog,
  isHouseholdBusinessInvoiceMissingBusinessLocation,
  shouldApplyHouseholdBusinessLocationRule,
} from "./DialogHouseholdBusinessLocationNotice";
import { Input } from "../item/Input";
import { ChiTietHangHoa } from "../item/ChiTietHangHoa";
import { AmountToWord } from "../../../service/AmountToWord";

import { FormatDisplay } from "../../../shared/format";
import { ConfirmDialog } from "primereact/confirmdialog";
import { ConfirmDialogService } from "../../../AppConfirmDialog";
import { InvoiceDiscountList } from "../../../service/invoice/InvoiceDiscountList";
import {
  bindStatementToInvoice,
  getStatementBuyerInfo,
  MultiInvoiceType,
} from "../../../utils/invoiceListBinding";
import { sanitizeCopiedInvoice } from "../../../utils/copiedInvoiceSanitizer";
import { MAX_INVOICES_PER_INVOICE_LIST } from "../../../utils/invoiceListSelectionLimit";
import _ from "lodash";
import DialogBangKe from "../../xu-ly-sai-sot/components/DialogBangKe";

// Form dùng mã nội bộ để tra label, nhưng dữ liệu cũ của hóa đơn vẫn nằm ở hai field legacy.
const getAdjustmentReasonValue = (data) =>
  data?.reasons ??
  data?.invoiceNote ??
  "";

function filterGroup04(obj) {
  // Kiểm tra nhóm "04" có tồn tại và có mảng settings hay không
  if (obj["04"] && Array.isArray(obj["04"].settings)) {
    obj["04"].settings = obj["04"].settings.filter(
      (setting) => setting.fieldName === "relatedInvoiceProperty",
    );
  }
  return obj;
}

function filterGroup04WithoutRelatedInvoiceProperty(obj) {
  if (obj["04"] && Array.isArray(obj["04"].settings)) {
    const disallowedFields = [
      "invoiceListDate",
      "invoiceListNumber",
      "relatedInvoiceListDate",
      "relatedInvoiceListNumber",
    ]; // những tên trường cần loại bỏ
    obj["04"].settings = obj["04"].settings.filter(
      (setting) => !disallowedFields.includes(setting.fieldName),
    );
  }
  return obj;
}

const createInvoiceListSetting = (fieldName, dataType, lable, baseSetting = {}) => ({
  ...baseSetting,
  fieldName,
  groupTypeCode: "04",
  dataType,
  lable,
  required: true,
  readOnly: true,
  lableWidth: baseSetting["lableWidth"] || 2,
  controlWidth: baseSetting["controlWidth"] || 4,
});

function ensureInvoiceListSettings(settings) {
  const seenFields = new Set<string>();
  const nextSettings = settings.filter((setting) => {
    if (seenFields.has(setting.fieldName)) {
      return false;
    }
    seenFields.add(setting.fieldName);
    return true;
  });
  const baseSetting =
    nextSettings.find((setting) => setting.fieldName === "relatedInvoiceProperty") ||
    nextSettings[0] ||
    {};

  if (!nextSettings.some((setting) => setting.fieldName === "relatedInvoiceListNumber")) {
    nextSettings.push(
      createInvoiceListSetting(
        "relatedInvoiceListNumber",
        "string",
        "Số bảng kê đính kèm",
        baseSetting,
      ),
    );
  }

  if (!nextSettings.some((setting) => setting.fieldName === "relatedInvoiceListDate")) {
    nextSettings.push(
      createInvoiceListSetting(
        "relatedInvoiceListDate",
        "datetime",
        "Ngày bảng kê đính kèm",
        baseSetting,
      ),
    );
  }

  return nextSettings;
}

function filterGroup04ForInvoiceList(obj) {
  if (obj["04"] && Array.isArray(obj["04"].settings)) {
    obj["04"].settings = ensureInvoiceListSettings(obj["04"].settings);
    const allowedFields = [
      "relatedInvoiceProperty",
      "relatedInvoiceListDate",
      "relatedInvoiceListNumber",
      "invoiceNote",
    ];
    const fieldOrder = [
      "relatedInvoiceProperty",
      "relatedInvoiceListNumber",
      "relatedInvoiceListDate",
      "invoiceNote",
    ];
    obj["04"].settings = obj["04"].settings
      .filter((setting) => {
        return allowedFields.includes(setting.fieldName);
      })
      .map((setting) => {
        const nextSetting = { ...setting };
        if (setting.fieldName === "relatedInvoiceProperty") {
          nextSetting.readOnly = false;
        } else {
          nextSetting.readOnly = true;
        }
        if (setting.fieldName === "relatedInvoiceListNumber") {
          nextSetting.labelTranslationKey = "common.invoiceListNo";
          nextSetting.required = true;
        }
        if (setting.fieldName === "relatedInvoiceListDate") {
          nextSetting.labelTranslationKey = "common.invoiceListDate";
          nextSetting.required = true;
        }
        if (setting.fieldName === "invoiceNote") {
          nextSetting.labelTranslationKey = "common.note";
        }
        return nextSetting;
      })
      .sort(
        (a, b) =>
          fieldOrder.indexOf(a.fieldName) - fieldOrder.indexOf(b.fieldName),
      );
  }
  return obj;
}

const invoiceListNumberFields = ["relatedInvoiceListNumber"];

const invoiceListDateFields = ["relatedInvoiceListDate"];

const getSettingLabel = (setting) =>
  setting?.labelTranslationKey
    ? String(i18n.t(setting.labelTranslationKey))
    : setting?.lable || setting?.label || setting?.header || setting?.fieldName;

const getRequiredMessage = (setting) =>
  i18n.t("validation.required", { field: getSettingLabel(setting) });

export class DialogCrup extends Component<any, any> {
  static defaultProps = {
    showSave: true,
  };

  /** Người dùng đã đóng dialog nhắc Hộ KD/CNKD trong phiên mở form hiện tại (mở lại form sẽ hiện lại nếu vẫn thiếu ĐĐKD). */
  private householdBusinessLocationNoticeUserDismissed = false;

  private tenantCompany;
  private invoiceGroup;
  private invoiceSettings;
  private emptyItem;
  private formulas;
  private taxes;

  protected onewayCalc;

  private draft;

  private totalAmountSetting;

  private tenantSetting = {};

  private defaultTax;

  private invoiceListBindingRequest = 0;

  private invoiceSubmissionInFlight = false;

  private petrolBusinessLocationRequestId = 0;

  /** Id mẫu đăng ký tương ứng với `invoiceSettings` hiện tại. */
  private loadedRegisterInvoiceId: string | null = null;

  /** Ghi nhận loại thao tác đã dùng để dựng field invoiceNote, tránh giữ cấu hình của hóa đơn gốc. */
  private loadedBts01ReasonCatalogType: number | null = null;

  private invoiceSettingsRequestId = 0;

  // MI18N: Dedupe request group labels theo language để tránh reload trùng khi mount/open/change language.
  private invoiceGroupLabelsPromise: Promise<void> | null = null;

  private invoiceGroupLabelsLanguage: string | null = null;

  private invoiceGroupLabelsRequestLanguage: string | null = null;

  private onLanguageChanged = () => {
    const registerInvoiceId = this.props.registerInvoice?.id;
    const shouldReloadInvoiceSettings =
      this.props.display && !!registerInvoiceId;

    // MI18N: Hủy response invoice-settings còn treo ở language cũ.
    this.invoiceSettingsRequestId += 1;

    if (registerInvoiceId && !shouldReloadInvoiceSettings) {
      // MI18N: Dialog đang đóng, đánh dấu stale để lần mở kế tiếp gọi lại BE theo language mới.
      this.loadedRegisterInvoiceId = null;
      this.loadedBts01ReasonCatalogType = null;
    }

    this.loadInvoiceGroupLabels({ skipLoading: true })
      .then(() => {
        if (!shouldReloadInvoiceSettings) {
          this.forceUpdate();
          return;
        }

        this.loadInvoiceSettingsForRegisterInvoice(registerInvoiceId, undefined, {
          preserveExistingItem: true,
          clearGroupSetting: false,
          skipLoading: true,
        });
      })
      .catch(() => {
        this.forceUpdate();
      });
  };

  private t = (key: string, options?: Record<string, any>): string =>
    String(i18n.t(key, options));

  constructor(props) {
    super(props);

    this.state = {
      displayBasic: false,
      windowHeight: window.innerHeight,
      submitting: false,
      invoiceGroupSetting: null,
      item: {},
      validators: {},
      relatedInvoiceDetail: null,
      displayDraft: false,
      invoiceType: null,
      invoiceTypeName: "",
      dialogConfirm: {
        visible: false,
        message: "",
      },
      invoiceListOptions: [],
      filteredInvoiceListOptions: [],
      displayInvoiceListConfirm: false,
      displayInvoiceListReview: false,
      pendingInvoiceListSign: false,
      showHouseholdBusinessLocationNotice: false,
      /** Chờ API địa điểm kinh doanh xong rồi mới quyết định hiện dialog (tránh nháy). */
      householdBusinessLocationCheckPending: false,
      petrolBusinessLocation: { code: "", address: "" },
    };

    this.onShow = this.onShow.bind(this);
    this.getFormat = this.getFormat.bind(this);
    this.patchItem = this.patchItem.bind(this);
    this.preview = this.preview.bind(this);
    this.onDraftShow = this.onDraftShow.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  //Sau khi component khởi tạo
  componentDidMount() {
    i18n.on("languageChanged", this.onLanguageChanged);
    // Lấy thông tin tenant -> đổ lên pahàn người bán
    TenantCompanyService.get(AppStorage.get("tenant")).then((data) => {
      this.tenantCompany = data;
    });

    // MI18N: Lấy group setting form động (0 -> 6 group) theo language hiện tại.
    this.loadInvoiceGroupLabels({ skipLoading: true });

    // MI18N: Nếu registerInvoice đã có sẵn khi mount, preload toàn bộ labels trước khi người dùng mở dialog.
    if (this.props.registerInvoice?.id) {
      this.loadInvoiceSettingsForRegisterInvoice(
        this.props.registerInvoice.id,
        undefined,
        { skipLoading: true },
      );
    }

    // Lấy mức thuế VAT
    TaxService.getList({ maxResultCount: 1000 }).then(({ items }) => {
      this.taxes = items;
      this.loadDefaultTax();
    });

    // ???
    TenantSettingService.getList({ maxResultCount: 1000 }).then(({ items }) => {
      this.tenantSetting = {};
      items.forEach((item) => {
        if (item.key) {
          this.tenantSetting[item.key] = item.value;
        }
      });
      this.loadDefaultTax();
      this.currencySyncDefault();
    });
  }

  componentWillUnmount() {
    i18n.off("languageChanged", this.onLanguageChanged);
  }

  private currencyGetDefault() {
    const defaultCode = String(
      this.tenantSetting[TenantSettingService.key.DEFAULT_MA_TIEN_TE] ?? "",
    )
      .trim()
      .toUpperCase();

    if (!defaultCode || !Array.isArray(this.props.currencies)) {
      return null;
    }

    return (
      this.props.currencies.find(
        (currency) =>
          String(currency?.code ?? "").trim().toUpperCase() === defaultCode,
      ) || null
    );
  }

  private currencySyncDefault() {
    if (!this.invoiceSettings || !Array.isArray(this.props.currencies)) {
      return;
    }

    const currencySetting = this.invoiceSettings.find(
      (setting) => setting.fieldName === InvoiceField.CurrencyCode,
    );
    if (currencySetting) {
      // Danh sách tiền tệ và invoice settings về độc lập nên phải đồng bộ option trước khi Dropdown render.
      currencySetting.options = this.props.currencies;
    }

    const defaultCurrency = this.currencyGetDefault();
    if (!defaultCurrency) {
      return;
    }

    this.emptyItem = {
      ...(this.emptyItem || { invoiceDetail: [] }),
      [InvoiceField.CurrencyCode]:
        this.emptyItem?.[InvoiceField.CurrencyCode] || defaultCurrency,
    };

    if (
      this.props.selectedItem?.id ||
      this.state.item?.[InvoiceField.CurrencyCode]
    ) {
      return;
    }

    this.setState((prevState) => {
      // Chỉ bổ sung khi form chưa có lựa chọn để không ghi đè giá trị người dùng hoặc hóa đơn đang sửa.
      if (prevState.item?.[InvoiceField.CurrencyCode]) {
        return null;
      }

      return {
        item: {
          ...prevState.item,
          [InvoiceField.CurrencyCode]: defaultCurrency,
        },
      };
    });
  }

  private loadInvoiceGroupLabels(options?: { skipLoading?: boolean }) {
    const language = i18n.language;

    // MI18N: Đã có labels đúng language thì dùng lại, không gọi BE thêm.
    if (
      this.invoiceGroup &&
      this.invoiceGroupLabelsLanguage === language
    ) {
      return Promise.resolve();
    }

    if (
      this.invoiceGroupLabelsPromise &&
      this.invoiceGroupLabelsRequestLanguage === language
    ) {
      return this.invoiceGroupLabelsPromise;
    }

    this.invoiceGroupLabelsRequestLanguage = language;
    // MI18N: API này trả label group động nên phải đi qua BE khi language đổi.
    const request = InvoiceSettingDefaultService.getGroupTypeCode({
      skipLoading: options?.skipLoading ?? true,
    }).then((data) => {
      this.invoiceGroup = {};
      Object.keys(data).forEach((key) => {
        this.invoiceGroup[key] = { label: data[key] };
      });
      this.invoiceGroupLabelsLanguage = language;
    });

    const trackedRequest = request.finally(() => {
      if (this.invoiceGroupLabelsPromise === trackedRequest) {
        this.invoiceGroupLabelsPromise = null;
        this.invoiceGroupLabelsRequestLanguage = null;
      }
    });
    this.invoiceGroupLabelsPromise = trackedRequest;
    return trackedRequest;
  }

  loadDefaultTax() {
    if (
      this.taxes &&
      this.taxes.length &&
      this.tenantSetting &&
      this.tenantSetting[TenantSettingService.key.DEFAULT_THUE_SUAT_GTGT]
    ) {
      this.taxes.forEach((tax) => {
        if (
          tax.code ===
          this.tenantSetting[TenantSettingService.key.DEFAULT_THUE_SUAT_GTGT]
        ) {
          this.defaultTax = tax;
        }
      });
    }
  }

  /** Map địa điểm kinh doanh từ API → item.businessLocation* (code = mã CH, name = tên CH, address = địa chỉ) */
  private buildBusinessLocationPatch(
    data: RegisterInvoiceBusinessLocation,
  ): Record<string, unknown> {
    return {
      [InvoiceField.businessLocationCode]: data.code ?? "",
      [InvoiceField.businessLocationName]: data.name ?? "",
      [InvoiceField.businessLocationAddress]: data.address ?? "",
    };
  }

  private shouldFetchBusinessLocationForSerial(serial?: string): boolean {
    const s = String(
      serial ??
        this.state.item?.invoiceSerial ??
        this.props.registerInvoice?.symbolCode ??
        "",
    );
    return shouldApplyHouseholdBusinessLocationRule(s);
  }

  private updateHouseholdBusinessLocationNoticeVisibility() {
    if (
      !this.props.display ||
      this.state.householdBusinessLocationCheckPending
    ) {
      return;
    }
    const blocked = isHouseholdBusinessInvoiceMissingBusinessLocation(
      this.state.item,
      this.props.registerInvoice?.symbolCode,
    );
    const shouldShowNotice =
      blocked && !this.householdBusinessLocationNoticeUserDismissed;
    if (shouldShowNotice !== this.state.showHouseholdBusinessLocationNotice) {
      this.setState({ showHouseholdBusinessLocationNotice: shouldShowNotice });
    }
  }

  private finishHouseholdBusinessLocationCheck() {
    this.setState({ householdBusinessLocationCheckPending: false }, () => {
      this.updateHouseholdBusinessLocationNoticeVisibility();
    });
  }

  private getInvoiceListType() {
    if (this.props.relatedInvoiceProperty === 3) {
      return 1;
    }
    if (this.props.relatedInvoiceProperty === 4) {
      return 2;
    }
    return null;
  }

  private isMultipleInvoiceListMode() {
    return [3, 4].includes(this.props.relatedInvoiceProperty);
  }

  private isCopiedInvoice() {
    return Boolean(
      this.props.selectedItem?.isClone ||
        this.props.selectedItem?.invoiceStatus === -1,
    );
  }

  private hasAttachedInvoiceList(data = this.state.item) {
    return (
      this.isMultipleInvoiceListMode() &&
      Boolean(
        data?.relatedInvoiceListId ||
          this.props.invoiceList?.id ||
          data?.relatedInvoiceListNumber ||
          this.props.invoiceList?.invoiceListNumber,
      )
    );
  }

  private shouldShowInvoiceListConfirmation(data = this.state.item) {
    return !this.isCopiedInvoice() && this.hasAttachedInvoiceList(data);
  }

  private isEditingMultipleInvoiceListInvoice() {
    return (
      this.isMultipleInvoiceListMode() &&
      Boolean(this.props.selectedItem?.id) &&
      !this.isCopiedInvoice()
    );
  }

  private getAttachedInvoiceListId() {
    return (
      this.state.item?.relatedInvoiceListId || this.props.invoiceList?.id || null
    );
  }

  private getRelatedInvoiceIdsFromProps() {
    const selectedInvoiceIds = (this.props.selectedInvoices || [])
      .map((item) => item?.originalInvoiceId || item?.invoiceId || item?.relatedInvoiceId || item?.id)
      .filter(Boolean);
    const invoiceListItemIds = (this.props.invoiceList?.invoiceListItems || [])
      .map((item) => item?.originalInvoiceId || item?.invoiceId || item?.relatedInvoiceId)
      .filter(Boolean);

    return Array.from(new Set([...selectedInvoiceIds, ...invoiceListItemIds]));
  }

  private getRelatedInvoiceIds(invoiceList) {
    const invoiceListItems = Array.isArray(invoiceList?.items)
      ? invoiceList.items
      : invoiceList?.invoiceListItems || [];
    const invoiceListItemIds = invoiceListItems
      .map(
        (item) =>
          item?.originalInvoiceId ||
          item?.invoiceId ||
          item?.relatedInvoiceId,
      )
      .filter(Boolean);
    const isInvoiceListFromProps =
      invoiceList?.id && invoiceList?.id === this.props.invoiceList?.id;
    const fallbackIds = isInvoiceListFromProps
      ? this.getRelatedInvoiceIdsFromProps()
      : [];

    return Array.from(new Set([...invoiceListItemIds, ...fallbackIds]));
  }

  private getInvoiceListActionType(): MultiInvoiceType {
    return this.props.relatedInvoiceProperty === 3 ? "replace" : "adjust";
  }

  private getTotalAmountToWord(item) {
    const totalAmount = item?.[InvoiceField.totalAmount];
    const currency = item?.[InvoiceField.CurrencyCode];

    if (
      typeof totalAmount !== "number" ||
      !currency ||
      !currency.code
    ) {
      return item?.[InvoiceField.totalAmountToWord] || "";
    }

    const formattedTotalAmount = totalAmount.toFixed(
      this.getFormat(this.totalAmountSetting, item),
    );

    return AmountToWord.DocTienBangChu(formattedTotalAmount, currency.code, {
      readCurrency: currency.readCurrency,
      readDecimal: currency.readDecimal,
    });
  }

  private buildInvoiceListBoundItem(invoiceList, currentItem) {
    const boundItem = bindStatementToInvoice({
      statement: invoiceList,
      invoiceType: this.getInvoiceListActionType(),
      currentInvoice: currentItem,
      taxes: this.taxes,
      defaultVatCode: this.defaultTax,
    });

    return {
      ...boundItem,
      [InvoiceField.totalAmountToWord]: this.getTotalAmountToWord(boundItem),
      relatedInvoiceListId: invoiceList?.id || "",
      relatedInvoiceListNumber:
        invoiceList?.statementNumber || invoiceList?.invoiceListNumber || "",
      relatedInvoiceListDate:
        invoiceList?.statementDate || invoiceList?.invoiceListDate
          ? new Date(
              invoiceList.statementDate || invoiceList.invoiceListDate,
            )
          : "",
      [InvoiceField.relatedInvoiceIds]: this.getRelatedInvoiceIds(invoiceList),
    };
  }

  private enrichInvoiceListBuyerInfo(invoiceList) {
    const originalInvoiceId = this.getRelatedInvoiceIds(invoiceList)[0];
    if (!originalInvoiceId) {
      return Promise.resolve(invoiceList);
    }

    return InvoiceService.getDetail(originalInvoiceId)
      .then((invoice) => ({
        ...getStatementBuyerInfo(invoice),
        ...invoiceList,
      }))
      .catch(() => invoiceList);
  }

  private loadAndBindInvoiceList(invoiceList, showError = true) {
    if (!invoiceList?.id) {
      return;
    }

    const request = ++this.invoiceListBindingRequest;
    Promise.all([
      InvoiceListService.get(invoiceList.id),
      InvoiceListService.getItems(invoiceList.id, {
        SkipCount: 0,
        MaxResultCount: MAX_INVOICES_PER_INVOICE_LIST,
        Sorting: "invoiceNumber",
        SortType: "DESCEND",
      }),
    ])
      .then(([data, itemsResult]) => {
        if (!data) {
          return data;
        }
        const items = Array.isArray(itemsResult)
          ? itemsResult
          : itemsResult?.items || [];
        return {
          ...data,
          items,
          invoiceListItems: items,
        };
      })
      .then((data) =>
        data ? this.enrichInvoiceListBuyerInfo(data) : data,
      )
      .then((data) => {
        if (
          request !== this.invoiceListBindingRequest ||
          !this.props.display ||
          !data
        ) {
          return;
        }
        this.setState((prevState) => ({
          item: this.buildInvoiceListBoundItem(data, prevState.item),
        }));
      })
      .catch(() => {
        if (request === this.invoiceListBindingRequest && showError) {
          ToastService.error(this.t("common.invoiceListLoadFailed"));
        }
      });
  }

  private loadPendingInvoiceLists() {
    this.setState({
      invoiceListOptions: [],
      filteredInvoiceListOptions: [],
    });
  }

  private searchInvoiceLists = (event) => {
    const invoiceType = this.getInvoiceListType();
    if (!invoiceType) {
      this.setState({
        invoiceListOptions: [],
        filteredInvoiceListOptions: [],
      });
      return;
    }
    InvoiceListService.getPending(invoiceType, event.query).then((data) => {
      const items = (data || []).map((item) => ({
        ...item,
        label: `${item.invoiceListNumber || ""}${
          item.invoiceListDate
            ? ` - ${FormatDisplay.date(item.invoiceListDate)}`
            : ""
        }`,
      }));
      this.setState({
        invoiceListOptions: items,
        filteredInvoiceListOptions: items,
      });
    });
  };

  private patchInvoiceList(invoiceList) {
    this.setState((prevState) => ({
      item: this.buildInvoiceListBoundItem(invoiceList, prevState.item),
    }));
    this.loadAndBindInvoiceList(invoiceList);
  }

  private patchInvoiceListNumber(fieldName, value) {
    // Hủy kết quả request của bảng kê trước nếu người dùng đã đổi nội dung nhập.
    this.invoiceListBindingRequest++;
    this.setState((prevState) => ({
      item: {
        ...prevState.item,
        [fieldName]: value,
        relatedInvoiceListNumber: value,
        relatedInvoiceListId: "",
      },
    }));
  }

  private patchInvoiceListDate(fieldName, value) {
    this.setState((prevState) => ({
      item: {
        ...prevState.item,
        [fieldName]: value,
        relatedInvoiceListDate: value,
      },
    }));
  }

  private findInvoiceListByNumber(value) {
    const invoiceListNumber =
      typeof value === "string"
        ? value
        : value?.invoiceListNumber || value?.label || "";
    const normalized = `${invoiceListNumber}`.trim().toLowerCase();
    if (!normalized) {
      return null;
    }
    return (this.state.invoiceListOptions || []).find(
      (item) =>
        `${item.invoiceListNumber || ""}`.trim().toLowerCase() === normalized,
    );
  }

  private patchMatchedInvoiceList(fieldName, value) {
    const matchedInvoiceList = this.findInvoiceListByNumber(value);
    if (matchedInvoiceList) {
      if (matchedInvoiceList.id === this.state.item.relatedInvoiceListId) {
        return true;
      }
      this.patchInvoiceList(matchedInvoiceList);
      return true;
    }
    this.patchInvoiceListNumber(
      fieldName,
      typeof value === "string" ? value : value?.invoiceListNumber || "",
    );
    return false;
  }

  private renderInvoiceListInput(setting, index) {
    const fieldName = setting.fieldName;
    const required = setting.required ? <span className="required">*</span> : null;
    const validator = this.state.validators?.[fieldName];
    const className = validator ? "p-invalid" : "";
    let input;

    if (invoiceListNumberFields.includes(fieldName)) {
      const value = this.state.item[fieldName] || "";
      const onChange = (e) => {
        if (e.value?.id) {
          this.patchInvoiceList(e.value);
          return;
        }
        this.patchInvoiceListNumber(fieldName, e.value || "");
      };

      input = (
        <div className="p-inputgroup">
          <AutoComplete
            id={fieldName}
            value={typeof value === "string" ? value : value.invoiceListNumber}
            disabled={this.isEditingMultipleInvoiceListInvoice()}
            suggestions={this.state.filteredInvoiceListOptions}
            completeMethod={this.searchInvoiceLists}
            field="invoiceListNumber"
            itemTemplate={(item) => (
              <div>
                <div>{item.invoiceListNumber}</div>
                {!!item.invoiceListDate && (
                  <small>{FormatDisplay.date(item.invoiceListDate)}</small>
                )}
              </div>
            )}
            onChange={onChange}
            onBlur={() =>
              this.patchMatchedInvoiceList(fieldName, this.state.item[fieldName])
            }
            className={className}
          />
          <span className="p-inputgroup-addon">
            <i className="pi pi-search" />
          </span>
        </div>
      );
    } else if (invoiceListDateFields.includes(fieldName)) {
      return (
        <Input
          key={index}
          item={this.state.item}
          getFormat={this.getFormat}
          patchItem={(group, field, value) =>
            this.patchInvoiceListDate(field, value)
          }
          setting={{ ...setting, readOnly: false }}
          validators={this.state.validators}
        />
      );
    }

    if (!input) {
      return (
        <Input
          key={index}
          item={this.state.item}
          getFormat={this.getFormat}
          patchItem={this.patchItem}
          setting={setting}
          validators={this.state.validators}
        />
      );
    }

    return (
      <Fragment key={index}>
        <label
          htmlFor={fieldName}
          className={`col-12 mb-2 md:col-${setting.lableWidth} md:mb-0`}
        >
          {setting.lable}
          {required}
        </label>
        <div className={`col-12 md:col-${setting.controlWidth}`}>
          {input}
          {!!validator && <small className="text-red-500">{validator}</small>}
        </div>
      </Fragment>
    );
  }

  /** Gọi khi mở form hóa đơn mới: điền địa điểm kinh doanh theo đăng ký (ký hiệu loại 2 / 5 / 6 / 7). */
  mergeBusinessLocationFromApi(customRegisterInvoiceId?: string, customSerial?: string) {
    let registerInvoiceId = customRegisterInvoiceId;
    const serial = String(
      customSerial ??
        this.state.item?.invoiceSerial ??
        this.props.registerInvoice?.symbolCode ??
        "",
    );
    if (!registerInvoiceId) {
      const foundRi = this.props.registerInvoices?.find(
        (ri) => ri.symbolCode === serial,
      );
      registerInvoiceId = foundRi?.id || this.props.registerInvoice?.id;
    }

    if (!this.shouldFetchBusinessLocationForSerial(serial)) {
      this.finishHouseholdBusinessLocationCheck();
      return;
    }
    if (!registerInvoiceId) {
      console.warn(
        "[DialogCrup] getBusinessLocation bỏ qua: không có registerInvoiceId",
      );
      this.finishHouseholdBusinessLocationCheck();
      return;
    }
    BusinessLocationService.getBusinessLocation(registerInvoiceId)
      .then((data: RegisterInvoiceBusinessLocation) => {
        if (!data) {
          console.warn("[DialogCrup] getBusinessLocation: response rỗng");
          return;
        }
        this.setState((prev) => {
          const patch = this.buildBusinessLocationPatch(data);
          return {
            item: {
              ...prev.item,
              ...patch,
            },
          };
        });
      })
      .catch((err) => {
        console.error("[DialogCrup] getBusinessLocation lỗi:", err);
      })
      .finally(() => {
        this.finishHouseholdBusinessLocationCheck();
      });
  }

  private isAdjustOrSubstituteInvoice() {
    return [2, 3].includes(this.props.invoiceStatus);
  }

  /** Tra thông tin địa điểm để đối chiếu trên form xăng dầu điều chỉnh/thay thế. */
  private syncPetrolBusinessLocation() {
    const requestId = ++this.petrolBusinessLocationRequestId;
    const shouldLoad =
      Boolean(this.props.display) &&
      Number(this.props.registerInvoice?.typeCompany) === 1 &&
      [2, 3].includes(Number(this.props.invoiceStatus));
    const registerInvoiceId = this.props.registerInvoice?.id;

    this.setState({ petrolBusinessLocation: { code: "", address: "" } });
    if (!shouldLoad || !registerInvoiceId) {
      return;
    }

    // Tra theo ID ký hiệu đang dùng để người lập chỉ đối chiếu thông tin địa điểm, không sửa hóa đơn.
    BusinessLocationService.getBusinessLocation(registerInvoiceId)
      .then((data: RegisterInvoiceBusinessLocation) => {
        if (requestId !== this.petrolBusinessLocationRequestId) {
          return;
        }
        this.setState({
          petrolBusinessLocation: {
            code: data?.code ?? "",
            address: data?.address ?? "",
          },
        });
      })
      .catch(() => {
        if (requestId === this.petrolBusinessLocationRequestId) {
          this.setState({ petrolBusinessLocation: { code: "", address: "" } });
        }
      });
  }

  private isBts01Invoice() {
    console.log('this.props.selectedItem?.typeCompany', this.props.selectedItem?.typeCompany);
    return Number(this.props.selectedItem?.typeCompany) === 9;
  }

  private shouldUseBts01ReasonCatalog() {
    return (
      [
        InvoiceService.documentType_Adjust,
        InvoiceService.documentType_Substitute,
      ].includes(this.props.invoiceStatus) &&
      !this.isMultipleInvoiceListMode() &&
      this.isBts01Invoice()
    );
  }

  private loadInvoiceSettingsForRegisterInvoice(
    registerInvoiceId: string,
    callback?: () => void,
    options?: {
      preserveExistingItem?: boolean;
      clearGroupSetting?: boolean;
      skipEmptyItem?: boolean;
      skipLoading?: boolean;
    },
  ) {
    const requestId = ++this.invoiceSettingsRequestId;
    const preserveExistingItem = options?.preserveExistingItem ?? false;
    const clearGroupSetting = options?.clearGroupSetting ?? true;
    const skipEmptyItem = options?.skipEmptyItem ?? false;

    if (clearGroupSetting && this.loadedRegisterInvoiceId !== registerInvoiceId) {
      this.setState({ invoiceGroupSetting: null });
    }

    // MI18N: Đợi group labels đúng language trước khi dựng lại form động từ tenant invoice settings.
    const isBts01Invoice = this.shouldUseBts01ReasonCatalog();
    const bts01ReasonCatalogType = isBts01Invoice
      ? this.props.invoiceStatus
      : null;

    console.log('isBts01Invoice', isBts01Invoice)

    return this.loadInvoiceGroupLabels({
      skipLoading: options?.skipLoading ?? false,
    }).then(() => RegisterInvoiceService.getInvoiceSettings(registerInvoiceId, {
      skipLoading: options?.skipLoading ?? false,
    })).then(
      (data) => {
        if (requestId !== this.invoiceSettingsRequestId) {
          return;
        }
        this.loadedRegisterInvoiceId = registerInvoiceId;
        this.loadedBts01ReasonCatalogType = bts01ReasonCatalogType;

        console.log('this.loadedRegisterInvoiceId', this.loadedRegisterInvoiceId)

        console.log('data.find((setting) => setting.fieldName === "invoiceNote")?.options', data.find((setting) => setting.fieldName === "invoiceNote")?.options)

        console.log('isBts01Invoice', isBts01Invoice)

        // Gắn type để ô lý do chỉ gọi catalog khi người dùng mở AutoComplete, tránh tải danh mục khi khởi tạo form.
        this.invoiceSettings = (Array.isArray(data) ? data : []).map((setting) => {
            return isBts01Invoice && setting.fieldName === "invoiceNote"
              ? { ...setting, options: [], type: bts01ReasonCatalogType }
              : setting;
          });
        if (!skipEmptyItem) {
          this.makeEmptyItem(undefined, preserveExistingItem);
        }
        this.makeInvoiceGroupSetting();
        this.currencySyncDefault();
        callback?.();
      },
    );
  }

  // Sau khi component update
  componentDidUpdate(
    prevProps: Readonly<any>,
    prevState: Readonly<any>,
    snapshot?: any,
  ) {
    if (prevProps.display !== this.props.display && !this.props.display) {
      this.syncPetrolBusinessLocation();
    }
    if (
      this.props.display &&
      (prevProps.invoiceStatus !== this.props.invoiceStatus ||
        prevProps.registerInvoice?.id !== this.props.registerInvoice?.id ||
        prevProps.registerInvoice?.typeCompany !==
          this.props.registerInvoice?.typeCompany)
    ) {
      // Khi sửa hóa đơn, loại điều chỉnh/thay thế được xác định sau lúc mở dialog nên cần tra lại địa điểm theo trạng thái mới.
      this.syncPetrolBusinessLocation();
    }

    // Chạy khi thay đổi loại hoá đơn
    // Lấy setting form động
    if (prevProps.registerInvoice !== this.props.registerInvoice) {
      if (this.props.registerInvoice?.id) {
        if (
          this.loadedRegisterInvoiceId !== this.props.registerInvoice.id
        ) {
          this.loadInvoiceSettingsForRegisterInvoice(
            this.props.registerInvoice.id,
          );
        }
      } else {
        this.invoiceSettingsRequestId += 1;
        this.loadedRegisterInvoiceId = null;
        this.loadedBts01ReasonCatalogType = null;
        this.setState({ invoiceGroupSetting: null, item: {} });
      }
    } else if (prevProps.invoiceList !== this.props.invoiceList) {
      if (
        this.props.registerInvoice?.id &&
        this.loadedRegisterInvoiceId === this.props.registerInvoice.id
      ) {
        this.makeEmptyItem(undefined, false);
        this.makeInvoiceGroupSetting();
      }
    }
    if (prevProps.currencies !== this.props.currencies) {
      this.currencySyncDefault();
    }
    if (
      (this.props.display && !prevProps.display && this.isMultipleInvoiceListMode()) ||
      prevProps.relatedInvoiceProperty !== this.props.relatedInvoiceProperty
    ) {
      this.loadPendingInvoiceLists();
    }
    if (prevState.item && this.state.item) {
      if (
        prevState.item.invoiceSerial !== this.state.item.invoiceSerial &&
        prevState.item.invoiceSerial
      ) {
        const isAdjustOrSubstitute = this.isAdjustOrSubstituteInvoice();
        this.householdBusinessLocationNoticeUserDismissed = false;
        if (
          this.shouldFetchBusinessLocationForSerial(
            this.state.item.invoiceSerial,
          )
        ) {
          this.setState({
            householdBusinessLocationCheckPending: true,
            showHouseholdBusinessLocationNotice: false,
          });
        }
        this.props.registerInvoices.forEach((ri) => {
          const cleanRi =
            typeof ri.symbolCode === "string" && ri.symbolCode.length === 7
              ? ri.symbolCode.substring(1)
              : ri.symbolCode;
          const cleanItemSerial =
            typeof this.state.item.invoiceSerial === "string" &&
            this.state.item.invoiceSerial.length === 7
              ? this.state.item.invoiceSerial.substring(1)
              : this.state.item.invoiceSerial;

          if (cleanRi === cleanItemSerial) {
            const sameRegisterInvoice =
              this.props.registerInvoice && ri.id === this.props.registerInvoice.id;
            const reloadSettings = this.loadedRegisterInvoiceId !== ri.id;

            const applySerialChange = () => {
              if (!isAdjustOrSubstitute) {
                this.makeEmptyItem(
                  {
                    invoiceSerial: this.state.item.invoiceSerial,
                  },
                  sameRegisterInvoice,
                );
              }
              this.makeInvoiceGroupSetting();
              this.mergeBusinessLocationFromApi();
            };

            if (reloadSettings) {
              this.loadInvoiceSettingsForRegisterInvoice(ri.id, applySerialChange, {
                preserveExistingItem: isAdjustOrSubstitute,
                clearGroupSetting: !isAdjustOrSubstitute,
                skipEmptyItem: isAdjustOrSubstitute,
              });
            } else {
              applySerialChange();
            }
          }
        });
        if (
          !this.shouldFetchBusinessLocationForSerial(
            this.state.item.invoiceSerial,
          )
        ) {
          this.finishHouseholdBusinessLocationCheck();
        }
      }
    }

    if (
      this.props.display &&
      this.state.invoiceGroupSetting &&
      !this.state.householdBusinessLocationCheckPending &&
      prevState.item &&
      this.state.item &&
      (prevState.item.businessLocationCode !==
        this.state.item.businessLocationCode ||
        prevState.item.businessLocationName !==
          this.state.item.businessLocationName ||
        prevState.item.businessLocationAddress !==
          this.state.item.businessLocationAddress)
    ) {
      this.updateHouseholdBusinessLocationNoticeVisibility();
    } else if (
      !this.props.display &&
      (this.state.showHouseholdBusinessLocationNotice ||
        this.state.householdBusinessLocationCheckPending)
    ) {
      this.setState({
        showHouseholdBusinessLocationNotice: false,
        householdBusinessLocationCheckPending: false,
      });
    }
  }

  getFormat(setting: any, _item?: any) {
    const item = _item ? _item : this.state.item;

    let maxFractionDigits = 0;
    if (
      setting &&
      setting.typeFormat &&
      item &&
      item[InvoiceField.CurrencyCode] &&
      item[InvoiceField.CurrencyCode][setting.typeFormat]
    ) {
      maxFractionDigits = item[InvoiceField.CurrencyCode][setting.typeFormat];
    }
    return maxFractionDigits;
  }

  makeEmptyItem(_default?: any, preserveExistingItem = true) {
    this.emptyItem = { invoiceDetail: [] };
    let def_c = null;
    console.log("this.props.currencies", this.props.currencies);
    console.log("this.tenantSetting[TenantSettingService.key.DEFAULT_MA_TIEN_TE]", this.tenantSetting[TenantSettingService.key.DEFAULT_MA_TIEN_TE])
    if (this.props.currencies) {
      this.props.currencies.forEach((c) => {
        if (
          c.code ===
          this.tenantSetting[TenantSettingService.key.DEFAULT_MA_TIEN_TE]
        ) {
          console.log("def_c", c);
          def_c = c;
        }
      });
    }

    this.invoiceSettings.forEach((setting) => {
      if ("05" !== setting.groupTypeCode) {
        let _emptyValue;

        switch (setting.fieldName) {
          case InvoiceField.totalVATDeduction:
            _emptyValue = 0;
            break;
          case InvoiceField.InvoiceSerial:
            _emptyValue = this.props.registerInvoice.symbolCode;
            break;
          case InvoiceField.CurrencyCode:
            _emptyValue = def_c;
            break;
          case InvoiceField.paymentMethod:
            _emptyValue =
              this.tenantSetting[TenantSettingService.key.DEFAULT_HTTT];
            break;
          case InvoiceField.exchangeRate:
            _emptyValue =
              this.tenantSetting[TenantSettingService.key.DEFAULT_TY_GIA];
            break;
          case InvoiceField.InvoiceDate:
            _emptyValue = new Date();
            break;
          case InvoiceField.nonTaxZone:
            _emptyValue =
              1 *
              this.tenantSetting[
                TenantSettingService.key.DEFAULT_KHU_PHI_THUE_QUAN
              ];
            break;
          case InvoiceField.isDeductionNQ43:
            _emptyValue =
              this.tenantSetting[
                TenantSettingService.key.DEFAULT_GIAM_THUE_NQ43
              ] === "true";
            break;
          default:
            switch (setting.dataType) {
              case "int32":
                _emptyValue = null;
                break;
              default:
                _emptyValue = "";
            }
        }

        this.emptyItem[setting.fieldName] = _emptyValue;
      }
    });

    if (this.tenantCompany) {
      this.emptyItem[InvoiceField.sellerTaxCode] = this.tenantCompany
        .useIdentityCard
        ? this.tenantCompany.identityCard
        : this.tenantCompany.taxCode;
      this.emptyItem[InvoiceField.sellerLegalName] =
        this.tenantCompany.name || "";
      this.emptyItem[InvoiceField.sellerAddress] =
        this.tenantCompany.address || "";
      this.emptyItem[InvoiceField.sellerEmail] = this.tenantCompany.email || "";
      this.emptyItem[InvoiceField.sellerTel] = this.tenantCompany.tel || "";
      this.emptyItem[InvoiceField.sellBankAccount] =
        this.tenantCompany.bankAccount || "";
      this.emptyItem[InvoiceField.sellerBankName] =
        this.tenantCompany.bankName || "";
      this.emptyItem[InvoiceField.sellerFax] = this.tenantCompany.fax || "";
      this.emptyItem[InvoiceField.sellerWebsite] =
        this.tenantCompany.webSite || "";
      this.emptyItem["relatedInvoiceListId"] =
        this.props.invoiceList?.id || "";
      this.emptyItem["relatedInvoiceListNumber"] =
        this.props.invoiceList?.invoiceListNumber || "";
      this.emptyItem["relatedInvoiceListDate"] = this.props.invoiceList
        ?.invoiceListDate
        ? new Date(this.props.invoiceList.invoiceListDate)
        : "";
      this.emptyItem[InvoiceField.relatedInvoiceIds] =
        this.getRelatedInvoiceIdsFromProps();
    }

    if (_default) {
      this.emptyItem = Object.assign(this.emptyItem, _default);
    }
    Object.assign(this.emptyItem, InvoiceService.DEFAULT_PAYMENT_FIELDS);
    const _emptyItem = Object.assign({}, this.emptyItem);
    const nextItem = preserveExistingItem
      ? Object.assign(_emptyItem, this.state.item)
      : _emptyItem;
    this.setState({ item: nextItem });
  }

  makeInvoiceGroupSetting() {
    const invoiceGroupSetting: any = { ...this.invoiceGroup };
    this.formulas = [];
    Object.keys(invoiceGroupSetting).forEach((key) => {
      invoiceGroupSetting[key].settings = [];
    });
    let invoiceTypeName;

    for (const setting of this.invoiceSettings) {
      if (
        setting.groupTypeCode === "05" &&
        INVOICE_DETAIL_REMOVED_FIELDS.includes(setting.fieldName)
      ) {
        continue;
      }
      switch (setting.fieldName) {
        case InvoiceField.CurrencyCode:
          setting.options = this.props.currencies;
          break;
        case InvoiceField.paymentMethod:
          setting.options = this.props.paymentTypes;
          break;
        case InvoiceField.InvoiceSerial:
          if (this.props.invoiceStatus) {
            const options = [];
            const invoiceType =
              this.state.invoiceType || (this.props.registerInvoice ? this.props.registerInvoice.invoiceType : undefined);
            const invoiceSerial = this.state.item.invoiceSerial;
            const cleanSerial =
              typeof invoiceSerial === "string" && invoiceSerial.length === 7
                ? invoiceSerial.substring(1)
                : invoiceSerial;
            const serialPrefix =
              typeof cleanSerial === "string" ? cleanSerial.charAt(0) : "";

            // eslint-disable-next-line no-loop-func
            this.props.registerInvoices.forEach((ri) => {
              const cleanRiSerial =
                typeof ri.symbolCode === "string" && ri.symbolCode.length === 7
                  ? ri.symbolCode.substring(1)
                  : ri.symbolCode;
              const riPrefix =
                typeof cleanRiSerial === "string" ? cleanRiSerial.charAt(0) : "";
              const riYear =
                typeof cleanRiSerial === "string"
                  ? cleanRiSerial.substring(1, 3)
                  : "";
              const invoiceYear = this.props.registerInvoice ? this.props.registerInvoice.invoiceYear : undefined;

              if (
                riPrefix === serialPrefix &&
                (invoiceYear === undefined || Number(riYear) >= Number(invoiceYear))
              ) {
                invoiceTypeName = ri.invoiceTypeName;
                options.push({ value: ri.symbolCode, label: ri.symbolCode });
              }
            });
            setting.options = options;
            setting.readOnly = true;
          } else {
            const invoiceType =
              this.state.invoiceType || (this.props.registerInvoice ? this.props.registerInvoice.invoiceType : undefined);
            // eslint-disable-next-line no-loop-func
            this.props.registerInvoices.forEach((ri) => {
              if (ri.invoiceType === invoiceType) {
                invoiceTypeName = ri.invoiceTypeName;
              }
            });
            setting.options = null;
            setting.readOnly = false;
          }
          break;
        case InvoiceField.relatedInvoiceProperty:
          setting.options = [
            { value: 1, translationKey: "common.replace" },
            { value: 2, translationKey: "common.adjust" },
            { value: 3, translationKey: "common.multipleInvoiceReplacement" },
            { value: 4, translationKey: "common.multipleInvoiceAdjustment" },
            { value: 5, translationKey: "common.commercialDiscountInvoice" },
          ];
          setting.readOnly = false;
          break;
        case InvoiceField.relatedInvoiceType:
          setting.options = [
            { value: 1, translationKey: "menu.electronicInvoiceN123" },
            { value: 2, translationKey: "menu.authenticatedInvoiceQD1209" },
            { value: 3, translationKey: "common.invoiceN51" },
            { value: 4, translationKey: "common.preprintedInvoiceN123" },
          ];
          break;
        case InvoiceField.totalAmount:
          if (setting.groupTypeCode === "06") {
            this.totalAmountSetting = setting;
          }
          break;
        case InvoiceField.nonTaxZone:
          setting.options = [
            { value: 1, translationKey: "common.inside" },
            { value: 0, translationKey: "common.outside" },
          ];
          break;
        case InvoiceField.isDeductionNQ43:
          setting.options = [
            { value: true, translationKey: "common.yes" },
            { value: false, translationKey: "common.no" },
          ];
          break;
        case InvoiceField.vatRateOverRevenue:
          setting.options = [
            { value: 1, label: "1" },
            { value: 2, label: "2" },
            { value: 3, label: "3" },
            { value: 5, label: "5" },
          ];
          break;
      }

      if (invoiceGroupSetting[setting.groupTypeCode]) {
        invoiceGroupSetting[setting.groupTypeCode].settings.push(setting);
        if (setting.formulas) {
          for (const formula of setting.formulas) {
            this.formulas.push({
              type: formula.formulaType,
              value: `${setting.groupTypeCode}.${setting.fieldName}`,
              from: formula.formulaBe,
              fromFe: formula.formulaFe,
              group: setting.groupTypeCode,
            });
          }
        }
        if (
          typeof this.state.item[InvoiceField.isDeductionNQ43] === "boolean" &&
          [
            InvoiceField.vatRateOverRevenue,
            InvoiceField.totalVATDeduction,
          ].includes(setting.fieldName)
        ) {
          setting.readOnly = this.state.item[InvoiceField.isDeductionNQ43];
        }
        if (
          typeof this.state.item[InvoiceField.isDeductionNQ43] === "boolean" &&
          [InvoiceField.vatRateOverRevenue].includes(setting.fieldName)
        ) {
          setting.required = this.state.item[InvoiceField.isDeductionNQ43];
        }
      }
    }
    if ([3, 4].includes(this.props.relatedInvoiceProperty)) {
      this.setState({
        invoiceGroupSetting: filterGroup04ForInvoiceList(invoiceGroupSetting),
        invoiceTypeName:
          invoiceTypeName || this.props.registerInvoice?.invoiceTypeName || "",
      });
    } else if ([5].includes(this.props.relatedInvoiceProperty)) {
      this.setState({
        invoiceGroupSetting: filterGroup04(invoiceGroupSetting),
          invoiceTypeName: this.t("menu.commercialDiscountInvoiceTypeName"),
      });
    } else {
      this.setState({
        invoiceGroupSetting:
          filterGroup04WithoutRelatedInvoiceProperty(invoiceGroupSetting),
        invoiceTypeName,
      });
    }
  }

  onShow() {
    this.syncPetrolBusinessLocation();

    // Mỗi lần mở form là một phiên binding mới; bỏ qua response còn treo từ
    // phiên trước để không ghi đè bảng kê hiện tại.
    this.invoiceListBindingRequest++;
    this.householdBusinessLocationNoticeUserDismissed = false;
    this.setState({
      showHouseholdBusinessLocationNotice: false,
      householdBusinessLocationCheckPending: true,
    });

    const registerInvoiceId = this.props.registerInvoice?.id;
    if (!registerInvoiceId) {
      this.finishHouseholdBusinessLocationCheck();
      return;
    }

    const shouldUseBts01ReasonCatalog = this.shouldUseBts01ReasonCatalog();
    const bts01ReasonCatalogType = shouldUseBts01ReasonCatalog
      ? this.props.invoiceStatus
      : null;
    // Cùng mẫu đăng ký nhưng đổi từ hóa đơn gốc sang điều chỉnh/thay thế thì phải dựng lại field invoiceNote.
    const shouldLoadInvoiceSettings =
      this.loadedRegisterInvoiceId !== registerInvoiceId ||
      this.loadedBts01ReasonCatalogType !== bts01ReasonCatalogType;

    if (shouldLoadInvoiceSettings) {
      this.loadInvoiceSettingsForRegisterInvoice(registerInvoiceId, () => {
        this.continueOnShow();
      });
      return;
    }
    this.continueOnShow();
  }

  continueOnShow() {
    this.currencySyncDefault();
    if (this.props.selectedItem && this.props.selectedItem.id) {
      if ([2, 3].includes(this.props.invoiceStatus)) {
        InvoiceService.getParentInvoice(this.props.selectedItem.id)
          .then((data) => {
            const isBts01Invoice = this.shouldUseBts01ReasonCatalog();
            if (isBts01Invoice) {
              data.invoiceNote = getAdjustmentReasonValue(data);
            }
            if (
              this.props.invoiceStatus ===
                InvoiceService.documentType_Substitute &&
              !this.isMultipleInvoiceListMode()
            ) {
              if (isBts01Invoice) {
                data.invoiceNote = "";
              } else {
                data.invoiceNote = "";
              }
            }
            if (data[InvoiceField.CurrencyId]) {
              this.props.currencies.forEach((currency) => {
                if (currency.id === data[InvoiceField.CurrencyId]) {
                  data[InvoiceField.CurrencyCode] = currency;
                }
              });
            }
            if (data.invoiceDetail) {
              data.invoiceDetail.forEach((item, index) => {
                item._id = `added-item-${Date.now()}_${Math.floor(Math.random() * 10000) + 1}_${index}`;
                Object.keys(item).forEach((k) => {
                  this.invoiceSettings.forEach((setting) => {
                    if (
                      setting.groupTypeCode === "05" &&
                      setting.fieldName === k &&
                      setting.dataType === "datetime" &&
                      item[k]
                    ) {
                      item[k] = new Date(item[k]);
                    }
                  });
                });
                if (this.taxes) {
                  this.taxes.forEach((tax) => {
                    if (tax.code === item[InvoiceField.vATCode]) {
                      item[InvoiceField.vATCode] = tax;
                    }
                  });
                }
              });
            }

            const item: any = {};
            Object.keys(data).forEach((k) => {
              this.invoiceSettings.forEach((setting) => {
                if (
                  setting.groupTypeCode !== "05" &&
                  setting.fieldName === k &&
                  setting.dataType === "datetime" &&
                  data[k]
                ) {
                  data[k] = new Date(data[k]);
                }
              });
              if (data[k] || data[k] === 0 || data[k] === false) {
                item[k] = data[k];
              } else {
                item[k] = "";
              }
            });
            let invoiceType;
            if (data.registerInvoiceId) {
              for (const ri of this.props.registerInvoices) {
                if (ri.id === data.registerInvoiceId) {
                  invoiceType = ri.invoiceType;
                  break;
                }
              }
            }
            this.setState({
              relatedInvoiceDetail: item.invoiceDetail,
              invoiceType,
            });
            item.invoiceDetail = [];
            this.invoiceSettings.forEach((setting) => {
              if (setting.groupTypeCode === "06") {
                item[setting.fieldName] = this.emptyItem[setting.fieldName];
              }
            });

            // hoa don lien quan
            // item[InvoiceField.relatedTemplateCode] = item[InvoiceField.templateCode];
            // item[InvoiceField.relatedInvoiceSerial] = item[InvoiceField.InvoiceSerial];
            item[InvoiceField.relatedInvoiceDate] =
              item[InvoiceField.InvoiceDate];
            item[InvoiceField.relatedInvoiceNumber] =
              "" + item[InvoiceField.invoiceNumber];
            item[InvoiceField.relatedInvoiceProperty] =
              this.props.invoiceStatus === 2 ? 2 : 1;
            item[InvoiceField.relatedInvoiceType] = 1;
            item[InvoiceField.relatedInvoiceId] = item.id;
            item[InvoiceField.InvoiceDate] = new Date();
            delete item.id;
            Object.assign(item, InvoiceService.DEFAULT_PAYMENT_FIELDS);

            this.setState({ item }, () => {
              this.makeInvoiceGroupSetting();
              const missingBusinessLocation = isHouseholdBusinessInvoiceMissingBusinessLocation(
                item,
                item.invoiceSerial,
              );
              if (missingBusinessLocation) {
                const serial = item.invoiceSerial;
                const cleanSerial =
                  typeof serial === "string" && serial.length === 7
                    ? serial.substring(1)
                    : serial;
                const ri = this.props.registerInvoices?.find((r) => {
                  const cleanRi =
                    typeof r.symbolCode === "string" && r.symbolCode.length === 7
                      ? r.symbolCode.substring(1)
                      : r.symbolCode;
                  return cleanRi === cleanSerial;
                });
                const registerInvoiceId =
                  ri?.id || data.registerInvoiceId || this.props.registerInvoice?.id;
                this.mergeBusinessLocationFromApi(registerInvoiceId, serial);
              } else {
                this.finishHouseholdBusinessLocationCheck();
              }
            });
          })
          .catch(() => this.finishHouseholdBusinessLocationCheck());
      } else {
        let detail;
        const invoiceType =
          this.state.invoiceType || (this.props.registerInvoice ? this.props.registerInvoice.invoiceType : undefined);
        const foundInvoice = this.props.registerInvoices.find(
          (ri) => ri.invoiceType === invoiceType,
        );

          switch (this.props.selectedItem.invoiceStatus) {
          case 2:
            // dieu chinh
            detail = this.isMultipleInvoiceListMode()
              ? InvoiceService.getDetail(this.props.selectedItem.id)
              : InvoiceService.getAdjustment(this.props.selectedItem.id);
            this.props.setInvoiceStatus?.(2);
            break;
          case 3:
            // thay the
            detail = this.isMultipleInvoiceListMode()
              ? InvoiceService.getDetail(this.props.selectedItem.id)
              : InvoiceService.getSubstitute(this.props.selectedItem.id);
            this.props.setInvoiceStatus?.(3);
            break;
          case -1:
            // copy hóa đơn
            detail = InvoiceService.getCopyInvoice(this.props.selectedItem.id);
            this.props.setInvoiceStatus?.(0);
            break;
          default:
            detail = InvoiceService.getDetail(this.props.selectedItem.id);
            // Khi xem lại hóa đơn hiện hữu từ màn hình Bảng kê, giữ nguyên
            // trạng thái nghiệp vụ của hóa đơn (ví dụ 6 = Substituted).
            // Các luồng mở form khác vẫn dùng trạng thái workflow mặc định 0.
            if (!this.props.invoiceListReviewOnly) {
              this.props.setInvoiceStatus?.(0);
            }
            break;
        }
        detail
          .then((data) => {
            const isBts01Invoice = this.shouldUseBts01ReasonCatalog();
            if (isBts01Invoice) {
              data.invoiceNote = getAdjustmentReasonValue(data);
            }
            const shouldResetPayment =
              this.props.selectedItem.isClone ||
              [-1, 2, 3].includes(this.props.selectedItem.invoiceStatus);

            if (shouldResetPayment) {
              Object.assign(data, InvoiceService.DEFAULT_PAYMENT_FIELDS);
            }

            if (this.props.selectedItem.isClone) {
              data = sanitizeCopiedInvoice(data);
              data.invoiceDate = new Date();
              delete data.invoiceNote;
              delete data.sendTaxStatus;
              delete data.taxAuthorityCode;
              delete data.sendEmail;
              delete data.securityNo;
              delete data.creatorName;
            }

            if (data[InvoiceField.CurrencyId]) {
              this.props.currencies.forEach((currency) => {
                if (currency.id === data[InvoiceField.CurrencyId]) {
                  data[InvoiceField.CurrencyCode] = currency;
                }
              });
            }
            if (data.invoiceDetail) {
              data.invoiceDetail.forEach((item, index) => {
                item._id = `added-item-${Date.now()}_${Math.floor(Math.random() * 10000) + 1}_${index}`;
                Object.keys(item).forEach((k) => {
                  this.invoiceSettings.forEach((setting) => {
                    if (
                      setting.groupTypeCode === "05" &&
                      setting.fieldName === k &&
                      setting.dataType === "datetime" &&
                      item[k]
                    ) {
                      item[k] = new Date(item[k]);
                    }
                  });
                });

                if (this.taxes) {
                  this.taxes.forEach((tax) => {
                    if (tax.code === item[InvoiceField.vATCode]) {
                      item[InvoiceField.vATCode] = tax;
                    }
                  });
                }
              });
            }

            const item: any = {};
            Object.keys(data).forEach((k) => {
              this.invoiceSettings.forEach((setting) => {
                if (
                  setting.groupTypeCode !== "05" &&
                  setting.fieldName === k &&
                  setting.dataType === "datetime" &&
                  data[k]
                ) {
                  data[k] = new Date(data[k]);
                }
              });
              if (data[k] || data[k] === 0 || data[k] === false) {
                item[k] = data[k];
              } else {
                item[k] = "";
              }
            });
            this.setState(
              {
                item,
                relatedInvoiceDetail: null,
                invoiceTypeName: foundInvoice?.invoiceTypeName,
              },
              () => {
                this.makeInvoiceGroupSetting();
                const missingBusinessLocation = isHouseholdBusinessInvoiceMissingBusinessLocation(
                  item,
                  item.invoiceSerial,
                );
                if (missingBusinessLocation) {
                  const serial = item.invoiceSerial;
                  const cleanSerial =
                    typeof serial === "string" && serial.length === 7
                      ? serial.substring(1)
                      : serial;
                  const ri = this.props.registerInvoices?.find((r) => {
                    const cleanRi =
                      typeof r.symbolCode === "string" && r.symbolCode.length === 7
                        ? r.symbolCode.substring(1)
                        : r.symbolCode;
                    return cleanRi === cleanSerial;
                  });
                  const registerInvoiceId =
                    ri?.id || data.registerInvoiceId || this.props.registerInvoice?.id;
                  this.mergeBusinessLocationFromApi(registerInvoiceId, serial);
                } else {
                  this.finishHouseholdBusinessLocationCheck();
                }
              },
            );
            if (
              typeof item[InvoiceField.isDeductionNQ43] === "boolean" &&
              this.state.invoiceGroupSetting &&
              this.state.invoiceGroupSetting["06"]
            ) {
              this.setState((prevState) => {
                const invoiceGroupSetting = prevState.invoiceGroupSetting;
                invoiceGroupSetting["06"].settings.forEach((setting) => {
                  if (
                    [InvoiceField.vatRateOverRevenue].includes(
                      setting.fieldName,
                    )
                  ) {
                    setting.required = item[InvoiceField.isDeductionNQ43];
                  }
                  if (
                    [
                      InvoiceField.vatRateOverRevenue,
                      InvoiceField.totalVATDeduction,
                    ].includes(setting.fieldName)
                  ) {
                    setting.readOnly = item[InvoiceField.isDeductionNQ43];
                  }
                });
                return {
                  invoiceGroupSetting: invoiceGroupSetting,
                };
              });
            }
          })
          .catch(() => this.finishHouseholdBusinessLocationCheck());
      }
    } else {
      let invoiceType;
      let item = { ...this.emptyItem };
      if ([2, 3].includes(this.props.invoiceStatus)) {
        invoiceType = this.props.registerInvoice ? this.props.registerInvoice.invoiceType : undefined;

        item[InvoiceField.relatedInvoiceProperty] =
          this.props.relatedInvoiceProperty ||
          (this.props.invoiceStatus === 2 ? 2 : 1);
        item[InvoiceField.relatedInvoiceType] = 3;
        if (this.isMultipleInvoiceListMode()) {
          if (this.props.invoiceList) {
            item = this.buildInvoiceListBoundItem(this.props.invoiceList, item);
          } else {
            item[InvoiceField.relatedInvoiceIds] =
              this.getRelatedInvoiceIdsFromProps();
          }
        }

        item[InvoiceField.InvoiceSerial] =
          this.props.registerInvoice ? this.props.registerInvoice.symbolCode : "";
      }
      if ([5].includes(this.props.relatedInvoiceProperty)) {
        this.makeInvoiceGroupSetting();
        console.log("🚀 ~ DialogCrup ~ onShow ~ item:", item);
        return this.setState(
          {
            item: {
              ...item,
              ..._.omit(this.props.idInvoiceListDiscount, "id"),
              invoiceListDate: new Date(
                this.props?.idInvoiceListDiscount?.invoiceListDate,
              ),
              invoiceListId: this.props?.idInvoiceListDiscount?.id,
              relatedInvoiceProperty: this.props.relatedInvoiceProperty,
            },
          },
          () => {
            this.mergeBusinessLocationFromApi();
          },
        );
      }
      this.setState({ item, relatedInvoiceDetail: null, invoiceType }, () => {
        this.makeInvoiceGroupSetting();
        this.mergeBusinessLocationFromApi();
        if (this.isMultipleInvoiceListMode() && this.props.invoiceList?.id) {
          this.loadAndBindInvoiceList(this.props.invoiceList, false);
        }
      });
    }
  }

  patchItem(group, field, value) {
    const _item = { ...this.state.item };

    switch (field) {
      case InvoiceField.relatedTemplateCode:
      case InvoiceField.relatedInvoiceSerial:
        _item[field] = value.toUpperCase();
        break;
      default:
        _item[field] = value;
    }

    if (field === InvoiceField.provinceCode) {
      // Đổi tỉnh làm thay đổi phạm vi danh mục xã nên không được giữ mã xã cũ.
      _item[InvoiceField.wardCode] = "";
    }

    switch (field) {
      case InvoiceField.CurrencyCode:
        if (value && value.id) {
          _item[InvoiceField.exchangeRate] = value.rate;
        }
        break;
      case InvoiceField.BuyerTaxCode:
      case InvoiceField.BuyerLegalName:
        if (value && value.buyerLegalName) {
          _item[InvoiceField.BuyerCode] = value.buyerCode || "";
          _item[InvoiceField.BuyerTaxCode] = value.buyerTaxCode || "";
          _item[InvoiceField.BuyerLegalName] = value.buyerLegalName || "";
          _item[InvoiceField.BuyerAddress] = value.buyerAddress || "";
          _item[InvoiceField.buyerEmail] = value.buyerEmail || "";
          _item[InvoiceField.buyerBankAccount] = value.buyerBankAccount || "";
          _item[InvoiceField.buyerBankName] = value.buyerBankName || "";
          _item[InvoiceField.buyerTel] = value.buyerTel || "";
          _item[InvoiceField.buyerDisplayName] = value.buyerDisplayName || "";
        }
        break;
      case InvoiceField.BuyerCode:
        if (value && value.id) {
          _item[InvoiceField.BuyerId] = value.id || "";
          _item[InvoiceField.BuyerCode] = value.code || "";
          _item[InvoiceField.BuyerTaxCode] = value.taxCode || "";
          _item[InvoiceField.BuyerLegalName] = value.name || "";
          _item[InvoiceField.buyerDisplayName] = value.personContact || "";
          _item[InvoiceField.BuyerAddress] = value.address || "";
          _item[InvoiceField.buyerEmail] = value.email || "";
          _item[InvoiceField.buyerBankAccount] = value.bankAccount || "";
          _item[InvoiceField.buyerBankName] = value.bankName || "";
          _item[InvoiceField.buyerTel] = value.mobile || "";
          _item[InvoiceField.buyerIdentityCard] = value.buyerIdentityCard || "";
          _item[InvoiceField.buyerBudgetUnitCode] =
            value.buyerBudgetUnitCode || "";
        }
        break;
      case InvoiceField.isDeductionNQ43:
        if (value === true) {
          this.setState((prevState) => {
            const invoiceGroupSetting = prevState.invoiceGroupSetting;
            invoiceGroupSetting["06"].settings.forEach((setting) => {
              if (
                [InvoiceField.vatRateOverRevenue].includes(setting.fieldName)
              ) {
                setting.required = true;
              }
              if (
                [
                  InvoiceField.vatRateOverRevenue,
                  InvoiceField.totalVATDeduction,
                ].includes(setting.fieldName)
              ) {
                setting.readOnly = true;
              }
            });
            return {
              invoiceGroupSetting: invoiceGroupSetting,
            };
          });
        } else if (value === false) {
          _item[InvoiceField.vatRateOverRevenue] = null;
          this.onValueChange(
            _item,
            "06",
            InvoiceField.vatRateOverRevenue,
            null,
          );
          this.setState((prevState) => {
            const invoiceGroupSetting = prevState.invoiceGroupSetting;
            invoiceGroupSetting["06"].settings.forEach((setting) => {
              if (
                [InvoiceField.vatRateOverRevenue].includes(setting.fieldName)
              ) {
                setting.required = false;
              }
              if (
                [
                  InvoiceField.vatRateOverRevenue,
                  InvoiceField.totalVATDeduction,
                ].includes(setting.fieldName)
              ) {
                setting.readOnly = false;
              }
            });
            return {
              invoiceGroupSetting: invoiceGroupSetting,
            };
          });
        }
        break;
    }

    this.onewayCalc = {};
    this.onValueChange(_item, group, field, value);

    if (
      field !== InvoiceField.totalAmountToWord &&
      typeof _item[InvoiceField.totalAmount] === "number" &&
      _item[InvoiceField.CurrencyCode] &&
      _item[InvoiceField.CurrencyCode].code
    ) {
      _item[InvoiceField.totalAmountToWord] =
        this.getTotalAmountToWord(_item);
      // _item[InvoiceField.totalAmountToWord] = AmountToWord.DocTienBangChu(_totalAmount, _item[InvoiceField.CurrencyCode].code);
    }
    this.setState({
      item: {
        ..._item,
      },
    });
    this.validateField(field, value);
  }

  hasParams(formula, group, field) {
    if (formula.group !== "05" && formula.fromFe) {
      for (let entry of JSON.parse(formula.fromFe)) {
        if (field === "invoiceDetail") {
          if (entry.type === "parameter" && entry.groupTypeCode === group) {
            return true;
          }
        } else {
          if (
            entry.type === "parameter" &&
            entry.groupTypeCode === group &&
            entry.fieldName === field
          ) {
            return true;
          }
        }
      }
    }
    return false;
  }

  onValueChange(_item, group, field, value) {
    if (!this.onewayCalc[field]) {
      this.onewayCalc[field] = 0;
    }
    if (this.onewayCalc[field] < 50) {
      this.onewayCalc[field]++;
      for (const formula of this.formulas) {
        if (this.hasParams(formula, group, field)) {
          let statement = "";
          try {
            const ROUND = (a) => {
              for (const setting of this.invoiceSettings) {
                if (
                  setting.fieldName === formula.value.split(".")[1] &&
                  setting.groupTypeCode === formula.value.split(".")[0]
                ) {
                  const fractionDigits = this.getFormat(setting);
                  a = parseFloat(parseFloat(a).toFixed(fractionDigits));
                }
              }
              return a;
            };

            let sumType = 0,
              sumStart = false,
              sumCondition = "";
            for (let entry of JSON.parse(formula.fromFe)) {
              if (entry.type === "parameter") {
                if (sumStart) {
                  let val = 0;
                  if (_item.invoiceDetail) {
                    for (const prd of _item.invoiceDetail) {
                      if (typeof prd[entry.fieldName] === "number") {
                        switch (sumType) {
                          case 1:
                            if (prd.property === 1 || prd.property === 5) {
                              val += prd[entry.fieldName];
                            } else if (prd.property === 3) {
                              val += prd[entry.fieldName] * -1;
                            }
                            break;
                          case 2:
                            let condition = sumCondition.split(" ");
                            // eslint-disable-next-line no-eval
                            if (
                              condition.length === 3 &&
                              eval(`${prd[condition[0]]}
														${condition[1]}
														${condition[2]}`)
                            ) {
                              if (prd.property === 1 || prd.property === 5) {
                                val += prd[entry.fieldName];
                              } else if (prd.property === 3) {
                                val += prd[entry.fieldName] * -1;
                              }
                            }
                            break;
                        }
                      }
                    }
                  }
                  statement += val;
                } else if (entry.fieldName === field) {
                  statement += value || 0;
                } else {
                  statement += _item[entry.fieldName] || 0;
                }
              } else if (entry.type === "operator" && entry.value === "SUM") {
                sumType = 1;
              } else if (entry.type === "operator" && entry.value === "SUMIF") {
                sumType = 2;
              } else {
                if (entry.type === "value" && sumType === 2 && sumStart) {
                  sumCondition = entry.value;
                } else {
                  statement += entry.value;
                }
                if (sumType && entry.type === "operator") {
                  if (entry.value === "(") {
                    sumStart = true;
                  }
                  if (entry.value === ")") {
                    sumStart = false;
                  }
                }
              }
            }
            // eslint-disable-next-line no-eval
            let result = eval(statement);
            if (typeof result === "number") {
              if (!Number.isFinite(result) || Number.isNaN(result)) {
                result = 0;
              }
              if (`${result}`.length > 2 && `${result}`.split(".").length) {
                result =
                  1 * result.toPrecision(`${result}`.split(".")[0].length + 7);
              }
              _item[formula.value.split(".")[1]] = result;
              this.onValueChange(
                _item,
                formula.value.split(".")[0],
                formula.value.split(".")[1],
                result,
              );
            }
          } catch (e) {
            console.log(statement, e);
          }
        }
      }
    }
  }

  // Required của nhóm thông tin phương tiện phụ thuộc vào mã phân loại trên từng dòng, không thể dùng một cờ cố định từ cấu hình API.
  private invoiceDetailReadOptionValue(item, field) {
    const value = item?.[field];
    if (value === undefined || value === null || value === "") {
      return null;
    }
    return Number(value);
  }

  private invoiceDetailIsFieldRequired(setting, item) {
    if (INVOICE_DETAIL_REMOVED_FIELDS.includes(setting.fieldName)) {
      return false;
    }

    const vehicleOrigin = this.invoiceDetailReadOptionValue(
      item,
      InvoiceField.vehicleOrigin,
    );
    const assetType = this.invoiceDetailReadOptionValue(
      item,
      InvoiceField.assetType,
    );
    const assetCondition = this.invoiceDetailReadOptionValue(
      item,
      InvoiceField.assetCondition,
    );

    switch (setting.fieldName) {
      case InvoiceField.safetyEnvCertNo:
        return vehicleOrigin === 1;
      case InvoiceField.factoryQCSerialNo:
        return vehicleOrigin === 0;
      case InvoiceField.payloadCapacity:
        // Trọng tải chỉ bắt buộc với ô tô đã qua sử dụng, không áp dụng cho mô tô xe máy.
        return assetType === 1 && assetCondition === 1;
      case InvoiceField.manufacturingCountryCode:
      case InvoiceField.yearProduce:
      case InvoiceField.registeredAssetTypeName:
      case InvoiceField.ProductName:
      case InvoiceField.brandName:
      case InvoiceField.vehicleModel:
      case InvoiceField.engineCapacityOrPower:
      case InvoiceField.seatingCapacity:
      case InvoiceField.vehicleLicensePlateNumber:
        return assetCondition === 1;
      default:
        return setting.required === true;
    }
  }

  validateField(field, value) {
    this.setState((prevState) => {
      const validators = prevState.validators;
      validators[field] = "";
      for (const setting of this.invoiceSettings) {
        if (setting.fieldName === field) {
          if (field === InvoiceField.BuyerTaxCode) {
            if (value?.trim && value?.trim()) {
              if (!/^[0-9-]+$/.test(value.trim()) || value.length > 14) {
                validators[setting.fieldName] = this.t("validation.taxCodeInvalid");
              }
            }
          }
          if (field === InvoiceField.buyerEmail) {
            value.split(/[;,]\s*/).forEach((email) => {
              if (
                !/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
                  email.trim(),
                )
              ) {
                validators[setting.fieldName] = this.t("validation.emailInvalid");
              }
            });
          }
          switch (setting.dataType) {
            case "int32":
              if (setting.required === true && isNaN(parseFloat(value))) {
                validators[setting.fieldName] = getRequiredMessage(setting);
              }
              break;
            default:
              if (setting.required === true) {
                if (value === "" || value === undefined) {
                  validators[setting.fieldName] = getRequiredMessage(setting);
                }
              }
          }
        }
      }
      if (!validators[field]) {
        validators[field] = this.validateSetting04(field, value);
      }
      return {
        validators: validators,
      };
    });
  }

  validateSetting04(field, value) {
    let validate = "";
    if (value && this.state.item && [2, 3].includes(this.props.invoiceStatus)) {
      switch (field) {
        case InvoiceField.relatedTemplateCode:
          if (this.state.item[InvoiceField.relatedInvoiceType] === 1) {
            if (
              !(
                value.length === 1 &&
                parseInt(value) >= 1 &&
                parseInt(value) <= 6
              )
            ) {
              validate = this.t("validation.wrongInvoiceDenominatorRange");
            }
          } else {
            if (value.length !== 11) {
              validate = this.t("validation.wrongInvoiceDenominatorLength");
            } else if (
              value.length >= 2 &&
              !["01", "02", "03", "04"].includes(`${value[0]}${value[1]}`)
            ) {
              validate = this.t("validation.wrongInvoiceFirstTypeChars");
            } else if (
              value.length >= 6 &&
              !["GTKT", "GTTT", "XKNB", "HGDL"].includes(
                `${value[2]}${value[3]}${value[4]}${value[5]}`.toUpperCase(),
              )
            ) {
              validate = this.t("validation.wrongInvoiceKindChars");
            } else if (
              value.length >= 7 &&
              !["0", "1", "2", "3"].includes(value[6])
            ) {
              validate = this.t("validation.wrongInvoiceCopiesChar");
            } else if (value.length >= 8 && !["/"].includes(value[7])) {
              validate = this.t("validation.wrongInvoiceSeparator");
            } else if (
              value.length >= 11 &&
              !["001", "002", "003"].includes(
                `${value[8]}${value[9]}${value[10]}`,
              )
            ) {
              validate = this.t("validation.wrongInvoiceIssueSequence");
            }
          }
          break;
        case InvoiceField.relatedInvoiceSerial:
          if (this.state.item[InvoiceField.relatedInvoiceType] === 1) {
            if (value.length !== 6) {
              validate = this.t("validation.wrongInvoiceRelatedSerialLength");
            } else if (!["C", "K"].includes(value[0].toUpperCase())) {
              validate = this.t("validation.wrongInvoiceFormChar");
            } else if (
              value.length >= 3 &&
              (Number.isNaN(parseInt(value[1])) ||
                Number.isNaN(parseInt(value[2])))
            ) {
              validate = this.t("validation.wrongInvoiceYearChar");
            } else if (
              value.length >= 4 &&
              !["T", "M", "G", "H", "B", "N", "D"].includes(
                value[3].toUpperCase(),
              )
            ) {
              validate = this.t("validation.wrongInvoiceTypeChar");
            }
          } else {
            if (value.length !== 6) {
              validate = this.t("validation.wrongInvoiceSerialLength");
            } else if (
              value.length >= 2 &&
              (!/^[A-EG-HK-NP-TVXYZ]$/.test(value[0].toUpperCase()) ||
                !/^[A-EG-HK-NP-TVXYZ]$/.test(value[1].toUpperCase()))
            ) {
              validate = this.t("validation.wrongInvoiceSerialPrefix");
            } else if (value.length >= 3 && !["/"].includes(value[2])) {
              validate = this.t("validation.wrongInvoiceSeparator");
            } else if (
              value.length >= 5 &&
              (Number.isNaN(parseInt(value[3])) ||
                Number.isNaN(parseInt(value[4])))
            ) {
              validate = this.t("validation.wrongInvoiceSerialYear");
            } else if (
              value.length === 6 &&
              !["E", "P", "T"].includes(value[5].toUpperCase())
            ) {
              validate = this.t("validation.wrongInvoiceSerialType");
            }
          }
          break;
        case InvoiceField.relatedInvoiceDate:
          if ([3, 4].includes(this.state.item[InvoiceField.relatedInvoiceProperty])) {
            return validate;
          }
          if (value) {
            if (new Date(value) > new Date()) {
              validate = this.t("validation.relatedInvoiceDateCannotBeFuture");
            }
          }
          break;
      }
    }
    return validate;
  }

  validateTaxCode(value) {
    let patt = /^[0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9]$/;
    if (value.length > 10) {
      patt =
        /^[0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][0-9][-][0-9][0-9][0-9]$/;
    }
    const cccdRegex = /^[0-9]{3}[0-5]\d{2}\d{6}$/;
    if (value === "") return true;

    // Kiểm tra nếu là căn cước công dân (12 ký tự)
    if (value.length === 12) {
      return cccdRegex.test(value);
    }

    // Kiểm tra mã số thuế
    if (patt.test(value) === false) {
      return false;
    }
    const number =
      Number(value.charAt(0)) * 31 +
      Number(value.charAt(1)) * 29 +
      Number(value.charAt(2)) * 23 +
      Number(value.charAt(3)) * 19 +
      Number(value.charAt(4)) * 17 +
      Number(value.charAt(5)) * 13 +
      Number(value.charAt(6)) * 7 +
      Number(value.charAt(7)) * 5 +
      Number(value.charAt(8)) * 3;

    return 10 - (number % 11) === Number(value.charAt(9));
  }

  validate() {
    if (this.state.item.invoiceDetail && this.state.item.invoiceDetail.length) {
      let validators = {},
        valid = true,
        firstRequiredMessage = "";

      const setRequiredValidator = (setting) => {
        valid = false;
        validators[setting.fieldName] = "Trường bắt buộc";
        if (!firstRequiredMessage) {
          firstRequiredMessage = getRequiredMessage(setting);
        }
      };

      if (
        this.state.item[InvoiceField.BuyerTaxCode] &&
        !this.validateTaxCode(this.state.item[InvoiceField.BuyerTaxCode])
      ) {
        ToastService.error(
          this.t("validation.invalid", { field: this.t("common.buyerTaxCode") }),
        );
        return false;
      }
      for (const setting of this.invoiceSettings) {
        if (setting.groupTypeCode === "05") {
          if (INVOICE_DETAIL_REMOVED_FIELDS.includes(setting.fieldName)) {
            continue;
          }

          for (const x of this.state.item.invoiceDetail) {
            if (!this.invoiceDetailIsFieldRequired(setting, x)) {
              continue;
            }

            if (
              x[InvoiceField.property] === 4 &&
              setting.fieldName !== InvoiceField.ProductName
            ) {
              continue;
            }

            const isMissing =
              setting.fieldName === InvoiceField.vATCode
                ? !x[setting.fieldName]
                : setting.dataType === "int32"
                  ? isNaN(parseFloat(x[setting.fieldName]))
                  : x[setting.fieldName] === undefined ||
                    x[setting.fieldName] === null ||
                    x[setting.fieldName] === "";

            if (isMissing) {
              ToastService.error(
                this.t("common.itemListFieldRequired", { field: setting.lable }),
              );
              valid = false;
              return false;
            }
          }
          continue;
        }
        if (
          (!this.props.invoiceStatus || this.props.invoiceStatus === 5) &&
          setting.groupTypeCode === "04"
        ) {
          continue;
        }

        if (setting.required) {
          switch (setting.dataType) {
            case "int32":
              if (isNaN(parseFloat(this.state.item[setting.fieldName]))) {
                setRequiredValidator(setting);
              }
              break;
            default:
              if (
                [3, 4].includes(this.props.relatedInvoiceProperty) &&
                setting.groupTypeCode === "04" &&
                ![
                  InvoiceField.relatedInvoiceProperty,
                  "invoiceNote",
                  ...invoiceListNumberFields,
                  ...invoiceListDateFields,
                ].includes(setting.fieldName)
              ) {
                continue;
              }
              if (
	                this.state.item[setting.fieldName] === undefined ||
	                this.state.item[setting.fieldName] === ""
	              ) {
	                setRequiredValidator(setting);
	              }
	          }
	        }
        if (!validators[setting.fieldName]) {
          validators[setting.fieldName] = this.validateSetting04(
            setting.fieldName,
            this.state.item[setting.fieldName],
          );
          if (validators[setting.fieldName]) {
            valid = false;
          }
	        }
	      }
	      this.setState({ validators });
	      if (!valid && firstRequiredMessage) {
	        ToastService.error(firstRequiredMessage);
	      }
	      return valid;
	    } else {
      ToastService.error(this.t("common.itemListEmpty"));
      return false;
    }
  }

  getData(): any {
    if (this.validate()) {
      const data: any = {},
        _data = Object.assign({}, this.state.item);
      Object.keys(_data).forEach((k) => {
        if (_data[k] === "") {
          _data[k] = null;
        }
        data[k] = _data[k];
      });
      data.registerInvoiceId = this.props.registerInvoice.id;
      if ([2, 3].includes(this.props.invoiceStatus)) {
        this.props.registerInvoices.forEach((ri) => {
          if (ri.symbolCode === data[InvoiceField.InvoiceSerial]) {
            data.registerInvoiceId = ri.id;
          }
        });
      }
      if (
        data[InvoiceField.CurrencyCode] &&
        data[InvoiceField.CurrencyCode].id
      ) {
        data[InvoiceField.CurrencyId] = data[InvoiceField.CurrencyCode].id;
        data[InvoiceField.CurrencyCode] = data[InvoiceField.CurrencyCode].code;
      }
      if (data[InvoiceField.BuyerCode] && data[InvoiceField.BuyerCode].id) {
        data[InvoiceField.BuyerId] = data[InvoiceField.BuyerCode].id;
        data[InvoiceField.BuyerCode] = data[InvoiceField.BuyerCode].code;
      }
      const invoiceDetail = [];
      data.invoiceDetail.forEach((x) => {
        const i: any = Object.assign({}, x);
        // Tên thương mại không bắt buộc với xe mới nên dùng các field nghiệp vụ khác để nhận diện dòng hàng.
        const hasDetailData = this.state.invoiceGroupSetting["05"].settings.some(
          (setting) =>
            ![
              InvoiceField.property,
              InvoiceField.isHiddenReport,
            ].includes(setting.fieldName) &&
            i[setting.fieldName] !== undefined &&
            i[setting.fieldName] !== null &&
            i[setting.fieldName] !== "",
        );
        if (hasDetailData) {
          if (i[InvoiceField.ProductCode] && i[InvoiceField.ProductCode].id) {
            i[InvoiceField.ProductId] = i[InvoiceField.ProductCode].id;
            i[InvoiceField.ProductCode] = i[InvoiceField.ProductCode].code;
          }
          if (i[InvoiceField.vATCode] && i[InvoiceField.vATCode].code) {
            i[InvoiceField.vATId] = i[InvoiceField.vATCode].id;
            i[InvoiceField.vATCode] = i[InvoiceField.vATCode].code;
            i[InvoiceField.VATRate] = i[InvoiceField.vATCode].code;
          }
          if (i[InvoiceField.unitCode] && i[InvoiceField.unitCode].value) {
            i[InvoiceField.unitCode] = i[InvoiceField.unitCode].value;
          }
          if (typeof i[InvoiceField.OrdinalNumber] === "number") {
            i[InvoiceField.OrdinalNumber] = "" + i[InvoiceField.OrdinalNumber];
          }

          Object.keys(i).forEach((key) => {
            if (i[key] instanceof Date) {
              i[key] = FormatDisplay.date(i[key], "YYYY-MM-DD");
            }
          });

          const validI = {
            formulaType: i.formulaType,
            orders: i.orders,
            isShowOrder: i.isShowOrder,
            ordinalNumberManual: i.ordinalNumberManual,
          };
          this.state.invoiceGroupSetting["05"].settings.forEach((setting) => {
            validI[setting.fieldName] = i[setting.fieldName];
          });

          invoiceDetail.push(validI);
        }
      });
      data.invoiceDetail = invoiceDetail;
      const isReviewingExistingInvoice =
        this.props.invoiceListReviewOnly &&
        this.props.selectedItem?.id &&
        !this.props.selectedItem?.isClone;
      data.invoiceStatus = isReviewingExistingInvoice
        ? this.props.selectedItem?.invoiceStatus ??
          this.state.item?.invoiceStatus ??
          this.props.invoiceStatus ??
          0
        : this.props.invoiceStatus;

      if ([3, 4].includes(this.props.relatedInvoiceProperty)) {
        data.relatedInvoiceProperty = this.props.relatedInvoiceProperty;
        data.relatedInvoiceListId =
          data.relatedInvoiceListId || this.props.invoiceList?.id;
        data.relatedInvoiceListNumber =
          data.relatedInvoiceListNumber ||
          this.props.invoiceList?.invoiceListNumber;
        data.relatedInvoiceListDate =
          data.relatedInvoiceListDate || this.props.invoiceList?.invoiceListDate;
        const relatedInvoiceIds = this.getRelatedInvoiceIdsFromProps();
        data.relatedInvoiceIds = data.relatedInvoiceIds?.length
          ? data.relatedInvoiceIds
          : relatedInvoiceIds;
      }

      Object.keys(data).forEach((key) => {
        if (data[key] instanceof Date) {
          data[key] = FormatDisplay.date(data[key], "YYYY-MM-DD");
        }
      });

      const isUpdating = data.id && !this.props.selectedItem?.isClone;
      if (!isUpdating) {
        Object.assign(data, InvoiceService.DEFAULT_PAYMENT_FIELDS);
      }

      return this.isCopiedInvoice() ? sanitizeCopiedInvoice(data) : data;
    }
  }

  preview(): void {
    const data = this.getData();
    if (data) {
      this.setState({ displayDraft: true });
    }
  }

  onDraftShow(): void {
    const data = this.getData();
    if (!this.state.submitting && data) {
      this.setState({ submitting: true });

      InvoiceService.printViewDraft(data)
        .then((res) => {
          let html = "";
          res.pagehmtl.forEach((page) => {
            html += page.trim();
          });

          this.draft.contentWindow.document.open();
          this.draft.contentWindow.document.write(html);
        })
        .finally(() => {
          this.setState({ submitting: false });
        });
    }
  }

  handleAlert(sign?: boolean) {
    if (
      this.state.householdBusinessLocationCheckPending ||
      isHouseholdBusinessInvoiceMissingBusinessLocation(
        this.state.item,
        this.props.registerInvoice?.symbolCode,
      )
    ) {
      ToastService.setMessage({
        summary: this.t("common.invoiceSaveBlockedSummary"),
        detail: this.t("common.businessLocationRequiredBeforeSave"),
      });
      return;
    }
    const data = this.getData();
    if (!data) {
      return;
    }
    const invoiceSerial = this.state.item.invoiceSerial;
    const invoiceSerialValue = invoiceSerial[0];
    if (
      this.props.registerInvoice.typeCompany !== 1 &&
      !this.props.invoiceStatus &&
      invoiceSerialValue === "1"
    ) {
      const invalidAmounts = data.invoiceDetail.filter((item) => {
        const calculatedAmount = item.quantity * item.unitPrice;
        return (
          calculatedAmount !== 0 &&
          Math.abs(calculatedAmount - item.amount) > 10
        );
      });

      const sumAmountWithoutVAT = data.invoiceDetail.reduce((sum, item) => {
        switch (item.property) {
          case 3:
            return sum - item.amountWithoutVAT;
          case 2:
          case 4:
            return sum;
          default:
            return sum + item.amountWithoutVAT;
        }
      }, 0);

      //invoiceDetail <>
      if (invalidAmounts.length) {
        return ConfirmDialogService.alert(
          this.t("common.notice"),
          invalidAmounts.map((item) => {
            const difference = Math.abs(
              item.quantity * item.unitPrice - item.amount,
            );
            return (
              <p>
                {this.t("common.invoiceAmountMismatch", {
                  product: item.productName,
                  line: item.ordinalNumber,
                  difference: FormatDisplay.number(difference),
                })}
              </p>
            );
          }),
          () => this.handleSubmit(sign),
        );
      }

      //Tiền trước thuế=Tổng tiền chưa thuế
      if (!(sumAmountWithoutVAT === data.totalAmountWithoutVAT)) {
        return ConfirmDialogService.alert(
          this.t("common.notice"),
          <p>{this.t("common.invoicePreTaxMismatch", {
            detail: FormatDisplay.number(sumAmountWithoutVAT),
            header: FormatDisplay.number(data.totalAmountWithoutVAT),
            difference: FormatDisplay.number(
              Math.abs(sumAmountWithoutVAT - data.totalAmountWithoutVAT),
            ),
          })}</p>,
          () => this.handleSubmit(sign),
        );
      }
      //Tổng tiền chưa thuế+Tổng tiền thuế=Tổng tiền thanh toán
      if (!(data.totalAmountWithoutVAT + data.vatAmount === data.totalAmount)) {
        return ConfirmDialogService.alert(
          this.t("common.notice"),
          <p>{this.t("common.invoiceTotalMismatch", {
            beforeTax: FormatDisplay.number(data.totalAmountWithoutVAT),
            tax: FormatDisplay.number(data.vatAmount),
            total: FormatDisplay.number(data.totalAmount),
            difference: FormatDisplay.number(
              Math.abs(
                data.totalAmountWithoutVAT + data.vatAmount - data.totalAmount,
              ),
            ),
          })}</p>,
          () => this.handleSubmit(sign),
        );
      }
    }

    if (
      this.props.registerInvoice.typeCompany === 4 &&
      data.invoiceDetail.find((item) => item.property === 5)
    ) {
      const itemsWithProperty5 = data.invoiceDetail.filter(
        (item) => item.property === 5,
      );
      const hasMissingFields = itemsWithProperty5.some((item) => {
        const missingFrameNumber =
          !item.frameNumber ||
          (typeof item.frameNumber === "string" && !item.frameNumber.trim());
        const missingEngineNumber =
          !item.engineNumber ||
          (typeof item.engineNumber === "string" && !item.engineNumber.trim());
        return missingFrameNumber || missingEngineNumber;
      });

      if (hasMissingFields) {
        return ConfirmDialogService.setProps({
          message:
            this.t("common.frameEngineRequiredForVehicle"),
          header: this.t("common.createInvoice"),
          icon: "pi pi-info-circle text-orange-400",
          acceptLabel: this.t("common.close"),
          rejectClassName: "hidden",
        });
      }
    }

    if (
      this.props.registerInvoice.typeCompany === 5 &&
      data.invoiceDetail.find((item) => item.property === 5)
    ) {
      const itemsWithProperty5 = data.invoiceDetail.filter(
        (item) => item.property === 5,
      );
      const hasMissingFields = itemsWithProperty5.some((item) => {
        const missingVehicleRegistrationNumber =
          !item.vehicleRegistrationNumber ||
          (typeof item.vehicleRegistrationNumber === "string" &&
            !item.vehicleRegistrationNumber.trim());
        return missingVehicleRegistrationNumber;
      });

      if (hasMissingFields) {
        return ConfirmDialogService.setProps({
          message:
            this.t("common.vehiclePlateRequiredForTransport"),
          header: this.t("common.createInvoice"),
          icon: "pi pi-info-circle text-orange-400",
          acceptLabel: this.t("common.close"),
          rejectClassName: "hidden",
        });
      }
    }

    if (
      this.props.registerInvoice.typeCompany === 6 &&
      data.invoiceDetail.find((item) => item.property === 5)
    ) {
      const itemsWithProperty5 = data.invoiceDetail.filter(
        (item) => item.property === 5,
      );
      const hasMissingFields = itemsWithProperty5.some((item) => {
        const missingSenderName =
          !item.senderName ||
          (typeof item.senderName === "string" && !item.senderName.trim());
        const missingSenderAddress =
          !item.senderAddress ||
          (typeof item.senderAddress === "string" &&
            !item.senderAddress.trim());
        const missingSenderTaxCode =
          !item.senderTaxCode ||
          (typeof item.senderTaxCode === "string" &&
            !item.senderTaxCode.trim());
        const missingSenderIdentifier =
          !item.senderIdentifier ||
          (typeof item.senderIdentifier === "string" &&
            !item.senderIdentifier.trim());

        return (
          missingSenderName ||
          missingSenderAddress ||
          missingSenderTaxCode ||
          missingSenderIdentifier
        );
      });

      if (hasMissingFields) {
        return ConfirmDialogService.setProps({
          message:
            this.t("common.senderInfoRequiredForDigitalTransport"),
          header: this.t("common.createInvoice"),
          icon: "pi pi-info-circle text-orange-400",
          acceptLabel: this.t("common.close"),
          rejectClassName: "hidden",
        });
      }
    }
    this.handleSubmit(sign);
  }

  handleSubmit(sign?: boolean, invoiceListConfirmed = false) {
    if (
      this.state.householdBusinessLocationCheckPending ||
      isHouseholdBusinessInvoiceMissingBusinessLocation(
        this.state.item,
        this.props.registerInvoice?.symbolCode,
      )
    ) {
      ToastService.setMessage({
        summary: this.t("common.invoiceSaveBlockedSummary"),
        detail: this.t("common.businessLocationRequiredBeforeSave"),
      });
      return;
    }
    const data = this.getData();
    if (!data) {
      if (invoiceListConfirmed) {
        this.setState({ displayInvoiceListConfirm: false });
      }
      return;
    }

    if (
      !invoiceListConfirmed &&
      this.shouldShowInvoiceListConfirmation(data)
    ) {
      this.setState({
        displayInvoiceListConfirm: true,
        pendingInvoiceListSign: Boolean(sign),
      });
      return;
    }

    if (!this.invoiceSubmissionInFlight && !this.state.submitting) {
      this.invoiceSubmissionInFlight = true;
      this.setState({ submitting: true });

      let method;

      if (this.props.relatedInvoiceProperty) {
        switch (this.props.invoiceStatus) {
          case 2:
            // dieu chinh
            if (data.id) {
              method = InvoiceService.updateAdjustment(data.id, data);
            } else {
              method = InvoiceService.createAdjustmentList(data);
            }
            break;
          case 3:
            // thay the
            if (data.id) {
              method = InvoiceService.updateSubstitute(data.id, data);
            } else {
              method = InvoiceService.createSubstituteList(data);
            }
            break;
          default:
            if (data.id && !this.props.selectedItem.isClone) {
              method = InvoiceService.update(data.id, data);
            } else {
              delete data.id;
              method = InvoiceService.create(data);
            }
        }
      } else {
        switch (this.props.invoiceStatus) {
          case 2:
            // dieu chinh
            if (data.id) {
              method = InvoiceService.updateAdjustment(data.id, data);
            } else {
              method = InvoiceService.createAdjustment(data);
            }
            break;
          case 3:
            // thay the
            if (data.id) {
              method = InvoiceService.updateSubstitute(data.id, data);
            } else {
              method = InvoiceService.createSubstitute(data);
            }
            break;
          // case 5:
          //   // thay the
          //   if (data.id) {
          //     method = InvoiceDiscountList.update(data.id, data);
          //   } else {
          //     method = InvoiceService.createSubstitute(data);
          //   }
          //   break;
          default:
            if (data.id && !this.props.selectedItem?.isClone) {
              if (this.props.relatedInvoiceProperty === 5) {
                method = InvoiceService.create(data);
                this.props.setInvoiceStatus?.(0);
                this.props.setRelatedInvoiceProperty?.(null);
                this.props.setDisplayDiscountInvoiceAttachment?.(false);
              } else {
                method = InvoiceService.update(data.id, data);
              }
            } else {
              if (this.props.relatedInvoiceProperty === 5) {
                method = InvoiceService.create({ ...data, invoiceStatus: 2 });
                this.props.setInvoiceStatus?.(0);
                this.props.setRelatedInvoiceProperty?.(null);
                this.props.setDisplayDiscountInvoiceAttachment?.(false);
                this.props.setIdInvoiceListDiscount?.(null);
              } else {
                delete data.id;
                method = InvoiceService.create(data);
              }
            }
        }
      }
      method
        .then((res) => {
          this.setState({ displayInvoiceListConfirm: false });
          if (sign) {
            let InvoiceIdArray;
            if (res.id) {
              InvoiceIdArray = [res];
            } else {
              InvoiceIdArray = [data];
            }
            ConfirmDialogService.confirmSign(() => {
              // Truyền dữ liệu form làm ngữ cảnh để kiểm tra hóa đơn điều chỉnh/thay thế nhiều.
              this.props.sign(
                {
                  CommandName: "SignInvoices",
                  InvoiceIdArray: InvoiceIdArray,
                },
                data,
              );
            });
            ToastService.success();
            this.props.setDisplay(false);
          } else {
            ToastService.success();
            this.props.setDisplay(false);
            this.props.reloadLazyData();
          }
        })
        .catch((error) => {
          ToastService.error(translateError(error?.response || error));
        })
        .finally(() => {
          this.invoiceSubmissionInFlight = false;
          this.setState({ submitting: false });
        });
    }
  }

  render() {
    if (this.state.invoiceGroupSetting) {
      let _header;
      switch (this.props.invoiceStatus) {
        case 2:
          _header = this.t("common.adjust");
          break;
        case 3:
          _header = this.t("common.replace");
          break;
        default:
          if (this.props.selectedItem && this.props.selectedItem.id) {
            if (this.props.selectedItem.isClone) {
              _header = this.t("common.copy");
            } else {
              _header = this.t("common.edit");
            }
          } else {
            _header = this.t("common.create");
          }
      }

      const multipleInvoiceHeaders = {
        3: this.t("menu.multipleInvoiceReplacement"),
        4: this.t("menu.multipleInvoiceAdjustment"),
      };
      const dialogHeader =
        multipleInvoiceHeaders[this.props.relatedInvoiceProperty] ||
        `${_header} ${this.state.invoiceTypeName}`;

      const householdBusinessSaveBlocked =
        this.state.householdBusinessLocationCheckPending ||
        isHouseholdBusinessInvoiceMissingBusinessLocation(
          this.state.item,
          this.props.registerInvoice?.symbolCode,
        );
      const showPetrolBusinessLocation =
        Number(this.props.registerInvoice?.typeCompany) === 1 &&
        [2, 3].includes(Number(this.props.invoiceStatus));
      // Địa điểm kinh doanh thuộc bên bán nên vẫn cần khung này khi mẫu không có trường bên bán động.
      const hasSellerInfo =
        this.state.invoiceGroupSetting["01"].settings.length > 0 ||
        showPetrolBusinessLocation;

      const footer = (
        <div className="flex justify-content-center">
          <Button
            label={this.t("common.close")}
            onClick={() => {
              this.props.setDisplay(false);
              this.props.setInvoiceStatus?.(0);
              this.props.setSelectedItems?.(null);
              this.props.setRelatedInvoiceProperty?.(null);
              this.props.setDisplayDiscountInvoiceAttachment?.(false);
              this.props.setIdInvoiceListDiscount?.(null);
              this.setState({
                validators: {},
                item: {},
                displayInvoiceListConfirm: false,
                displayInvoiceListReview: false,
              });
            }}
            className="p-button-outlined p-button-secondary p-button-sm mr-2"
          />
          <Button
              label={this.t("common.preview")}
            onClick={this.preview}
            loading={this.state.submitting}
            className="p-button-outlined p-button-success p-button-sm mr-2"
          />
          {this.props.showSave && (
            <Button
              label={this.t("common.save")}
              onClick={() => this.handleAlert()}
              loading={this.state.submitting}
              disabled={householdBusinessSaveBlocked}
              className="p-button-outlined p-button-sm mr-2"
            />
          )}
          {this.props.showIconSign && (
            <Button
              label={this.t("common.saveAndSign")}
              onClick={() => this.handleAlert(true)}
              loading={this.state.submitting}
              disabled={householdBusinessSaveBlocked}
              className="p-button-sm"
            />
          )}
        </div>
      );
      return (
        <Fragment>
          <HouseholdBusinessLocationNoticeDialog
            visible={this.state.showHouseholdBusinessLocationNotice}
            onHide={() => {
              this.householdBusinessLocationNoticeUserDismissed = true;
              this.setState({ showHouseholdBusinessLocationNotice: false });
            }}
          />
          <Dialog
            header={dialogHeader}
            footer={footer}
            visible={this.props.display}
            maximized={true}
            onShow={this.onShow}
            onHide={() => {
              this.props.setDisplay(false);
              this.props.setInvoiceStatus?.(0);
              this.props.setSelectedItems?.(null);
              this.props.setRelatedInvoiceProperty?.(null);
              this.props.setDisplayDiscountInvoiceAttachment?.(false);
              this.props.setIdInvoiceListDiscount?.(null);
              this.setState({
                validators: {},
                item: {},
                displayInvoiceListConfirm: false,
                displayInvoiceListReview: false,
              });
            }}
            className="form-xs bg-theme crup-invoice"
          >
            <div className="grid p-1">
              <div
                className={`col-12 p-1 ${hasSellerInfo ? "md:col-2" : "md:col-3"}`}
              >
                <Fieldset legend={this.t("common.generalInfo")}>
                  <div className="p-fluid grid">
                    {this.state.invoiceGroupSetting["00"].settings.map(
                      (setting, index) => {
                        return (
                          <Input
                            key={index}
                            item={this.state.item}
                            getFormat={this.getFormat}
                            patchItem={this.patchItem}
                            setting={setting}
                            validators={this.state.validators}
                          />
                        );
                      },
                    )}
                  </div>
                </Fieldset>
              </div>
              {hasSellerInfo && (
                <div className="col-12 md:col-5 p-1">
                  <Fieldset legend={this.t("common.sellerInfo")}>
                    <div className="p-fluid grid">
                      {this.state.invoiceGroupSetting["01"].settings.map(
                        (setting, index) => {
                          return (
                            <Input
                              key={index}
                              item={this.state.item}
                              getFormat={this.getFormat}
                              patchItem={this.patchItem}
                              setting={setting}
                              validators={this.state.validators}
                            />
                          );
                        },
                      )}
                      {showPetrolBusinessLocation && (
                        <>
                          <label
                            htmlFor="petrolBusinessLocationCode"
                            className="col-12 mb-2 md:col-2 md:mb-0"
                          >
                            {this.t("catalog.businessLocationCode")}
                          </label>
                          <div className="col-12 md:col-10">
                            <InputText
                              id="petrolBusinessLocationCode"
                              value={this.state.petrolBusinessLocation.code}
                              placeholder={this.t("catalog.businessLocationHint")}
                              className="w-full"
                              disabled
                            />
                          </div>
                          <label
                            htmlFor="petrolBusinessLocationAddress"
                            className="col-12 mb-2 md:col-2 md:mb-0"
                          >
                            {this.t("catalog.businessLocationAddress")}
                          </label>
                          <div className="col-12 md:col-10">
                            <InputText
                              id="petrolBusinessLocationAddress"
                              value={this.state.petrolBusinessLocation.address}
                              placeholder={this.t("catalog.businessLocationHint")}
                              className="w-full"
                              disabled
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </Fieldset>
                </div>
              )}
              <div
                className={`col-12 p-1 ${hasSellerInfo ? "md:col-5" : "md:col-9"}`}
              >
                <Fieldset legend={this.t("common.buyerInfo")}>
                  <div className="p-fluid grid">
                    {this.state.invoiceGroupSetting["02"].settings.map(
                      (setting, index) => {
                        return (
                          <Input
                            key={index}
                            item={this.state.item}
                            getFormat={this.getFormat}
                            patchItem={this.patchItem}
                            setting={setting}
                            validators={this.state.validators}
                          />
                        );
                      },
                    )}
                  </div>
                </Fieldset>
              </div>
              {this.state.invoiceGroupSetting["04"].settings.length &&
                [2, 3].includes(this.props.invoiceStatus) && (
                  <div className="col-12 p-1">
                    <Fieldset legend={this.t("common.relatedInvoiceInfo")}>
                      <div className="p-fluid grid">
                        {this.state.invoiceGroupSetting["04"].settings.map(
                          (setting, index) => {
                            if (this.isMultipleInvoiceListMode()) {
                              return this.renderInvoiceListInput(setting, index);
                            }
                            return (
                              <Input
                                key={index}
                                item={this.state.item}
                                getFormat={this.getFormat}
                                patchItem={this.patchItem}
                                setting={setting}
                                validators={this.state.validators}
                              />
                            );
                          },
                        )}
                      </div>
                    </Fieldset>
                  </div>
                )}
              {this.state.invoiceGroupSetting["04"].settings.length &&
                !this.isMultipleInvoiceListMode() &&
                (this?.props?.selectedItem?.relatedInvoiceProperty === 5 ||
                  this.props.relatedInvoiceProperty === 5) && (
                  <div className="col-12 p-1">
                    <Fieldset legend={this.t("common.relatedInvoiceInfo")}>
                      <div className="p-fluid grid">
                        {this.state.invoiceGroupSetting["04"].settings
                          .filter(
                            (setting) =>
                              ![
                                "Loại hóa đơn liên quan",
                                "Ngày lập hóa đơn liên quan",
                                "Mẫu số hóa đơn liên quan",
                                "Ký hiệu hóa đơn liên quan",
                                "Số hóa đơn liên quan",
                                "Lý do sai sót",
                              ].includes(setting.lable),
                          )
                          .map((setting, index) => {
                            return (
                              <Input
                                key={index}
                                item={this.state.item}
                                getFormat={this.getFormat}
                                patchItem={this.patchItem}
                                setting={setting}
                                validators={this.state.validators}
                              />
                            );
                          })}
                      </div>
                    </Fieldset>
                  </div>
                )}
              {!!this.state.invoiceGroupSetting["03"].settings.length && (
                <div className="col-12 p-1">
                  <Fieldset legend={this.t("common.otherInfo")}>
                    <div className="p-fluid grid">
                      {this.state.invoiceGroupSetting["03"].settings.map(
                        (setting, index) => {
                          return (
                            <Input
                              key={index}
                              item={this.state.item}
                              getFormat={this.getFormat}
                              patchItem={this.patchItem}
                              setting={setting}
                              validators={this.state.validators}
                            />
                          );
                        },
                      )}
                    </div>
                  </Fieldset>
                </div>
              )}
              <div className="col-12 p-1">
                <ChiTietHangHoa
                  invoiceGroupSetting={this.state.invoiceGroupSetting}
                  formulas={this.formulas}
                  item={this.state.item}
                  taxes={this.taxes}
                  defaultTax={this.defaultTax}
                  patchItem={this.patchItem}
                  getFormat={this.getFormat}
                  invoiceStatus={this.props.invoiceStatus}
                  relatedInvoiceDetail={this.state.relatedInvoiceDetail}
                  registerInvoiceId={this?.props?.registerInvoice?.id}
                  registerInvoice={this.props.registerInvoice}
                />
              </div>
              <div className="col-12 p-1">
                <Fieldset legend={this.t("common.grandTotal")}>
                  <div className="p-fluid grid">
                    {this.state.invoiceGroupSetting["06"].settings.map(
                      (setting, index) => {
                        return (
                          <Input
                            key={index}
                            item={this.state.item}
                            getFormat={this.getFormat}
                            patchItem={this.patchItem}
                            setting={setting}
                            validators={this.state.validators}
                            gridType="percent"
                          />
                        );
                      },
                    )}
                  </div>
                </Fieldset>
              </div>
            </div>
          </Dialog>
          <Dialog
            header={
              <span className="flex align-items-center justify-content-center gap-2">
                {this.t("common.confirmCreateInvoiceWithList")}
              </span>
            }
            visible={this.state.displayInvoiceListConfirm}
            style={{ width: "560px", maxWidth: "90vw" }}
            draggable={false}
            resizable={false}
            closable={false}
            closeOnEscape={!this.state.submitting}
            onHide={() => {
              if (!this.state.submitting) {
                this.setState({ displayInvoiceListConfirm: false });
              }
            }}
            footer={
              <div className="flex justify-content-center gap-2">
                <Button
                  label={this.t("common.cancel")}
                  className="p-button-outlined p-button-secondary p-button-sm"
                  disabled={this.state.submitting}
                  onClick={() =>
                    this.setState({ displayInvoiceListConfirm: false })
                  }
                />
                <Button
                  label={this.t("common.reviewInvoiceList")}
                  className="p-button-outlined p-button-info p-button-sm"
                  disabled={
                    this.state.submitting || !this.getAttachedInvoiceListId()
                  }
                  onClick={() =>
                    this.setState({
                      displayInvoiceListConfirm: false,
                      displayInvoiceListReview: true,
                    })
                  }
                />
                <Button
                  label={this.t("common.confirmation")}
                  className="p-button-sm"
                  loading={this.state.submitting}
                  disabled={this.state.submitting}
                  onClick={() => {
                    if (this.invoiceSubmissionInFlight) {
                      return;
                    }
                    this.handleSubmit(
                      this.state.pendingInvoiceListSign,
                      true,
                    );
                  }}
                />
              </div>
            }
          >
            <div className="text-lg">
              <p className="mt-0 line-height-3">
                {this.t("common.checkInvoiceAndList")}
              </p>
              <p className="line-height-3">{this.t("common.afterConfirmation")}</p>
              <ul className="line-height-3">
                <li>{this.t("common.updateInvoiceListStatus")}</li>
                <li>{this.t("common.invoiceListLockedAfterConfirm")}</li>
              </ul>
              <p className="line-height-3">
                {this.t("common.deleteMultipleInvoiceToEditList")}
              </p>
              <p className="mb-0 line-height-3">
                <strong>{this.t("common.saveInvoiceQuestion")}</strong>
              </p>
            </div>
          </Dialog>
          <DialogBangKe
            display={this.state.displayInvoiceListReview}
            setDisplay={(display) =>
              this.setState({ displayInvoiceListReview: display })
            }
            invoiceListId={this.getAttachedInvoiceListId()}
            invoiceType={this.getInvoiceListType()}
            registerInvoices={this.props.registerInvoices}
            viewOnly={Boolean(
              this.props.invoiceListReviewOnly ||
                this.isEditingMultipleInvoiceListInvoice(),
            )}
            onSubmit={() => {
              const invoiceListId = this.getAttachedInvoiceListId();
              this.setState({ displayInvoiceListReview: false });
              if (invoiceListId) {
                this.loadAndBindInvoiceList({ id: invoiceListId });
              }
            }}
          />
          <Dialog
            visible={this.state.displayDraft}
            style={{ width: "890px" }}
            draggable={false}
            resizable={true}
            position="top"
            onHide={() => this.setState({ displayDraft: false })}
            onShow={this.onDraftShow}
            className="view-invoice"
          >
            <iframe
              className="template-preview"
              ref={(el) => (this.draft = el)}
              title="template-preview"
            />
          </Dialog>
          {/* <ConfirmDialog
            visible={this.state.dialogConfirm.visible}
            onHide={() => this.setState({ dialogConfirm: { ...this.state.dialogConfirm, visible: false } })}
            message={this.state.dialogConfirm.message}
            header="Thông báo"
            icon="pi pi-exclamation-triangle"
            accept={() => this.setState({ dialogConfirm: { ...this.state.dialogConfirm, visible: false } })}
            reject={() => this.setState({ dialogConfirm: { ...this.state.dialogConfirm, visible: false } })}
          /> */}
        </Fragment>
      );
    } else {
      return "";
    }
  }
}
